import { useEffect, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { readStaleSessionCache, writeSessionCache } from "@/lib/sessionCache";
import { SkeletonTable } from "@/components/Skeletons";
import PageLayout from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { FileText, Upload, CheckCircle, XCircle, Copy, Search, Mail, Calendar, DollarSign, Phone, Download, Trash2, AlertTriangle, AlertCircle, Clock, Plus, ArrowUpAZ, ArrowDownAZ, ArrowUpNarrowWide, ArrowDownNarrowWide } from "lucide-react";
import { useYear } from "@/contexts/YearContext";
import EmailPreviewModal from "@/components/EmailPreviewModal";
import FileDropZone from "@/components/FileDropZone";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Template email
const EMAIL_TEMPLATES = {
  documenti_firmare: {
    subject: "Documenti da firmare – intervento presso la Sua abitazione",
    body: `Gentile Cliente,

Le abbiamo appena inviato i documenti relativi al Suo intervento (contratto / conferma d'ordine / documentazione tecnica), che necessitano di firma per poter procedere correttamente.

La invitiamo a:
• verificare i documenti ricevuti
• firmarli
• rinviarceli quanto prima

Una volta ricevuta la documentazione firmata, potremo proseguire con le fasi successive senza rallentamenti.

Per qualsiasi dubbio o chiarimento restiamo a Sua completa disposizione.

Cordiali saluti
Showroom Ingenio`
  },
  calendario_posa: {
    subject: "Programmazione posa infissi – conferma data",
    body: `Gentile Cliente,

La informiamo che è stata programmata la posa dei Suoi infissi secondo il seguente calendario:

📅 Data di posa: come da accordi
📍 Luogo di intervento: Sua abitazione

Qualora avesse necessità di segnalare impedimenti o richieste particolari, La invitiamo a comunicarcelo tempestivamente, così da evitare disallineamenti o ritardi organizzativi.

In assenza di comunicazioni, la data indicata si intenderà confermata.

Restiamo a disposizione.
Cordiali saluti
Showroom Ingenio`
  },
  sollecito_pagamento: {
    subject: "Promemoria pagamento – pratica in corso",
    body: `Gentile Cliente,

con la presente desideriamo ricordarLe che, alla data odierna, il pagamento relativo alla Sua pratica risulta ancora non pervenuto.

Il saldo è necessario per garantire:
• la continuità della fornitura
• il rispetto delle tempistiche concordate
• la corretta pianificazione delle attività operative

Qualora il pagamento fosse già stato effettuato, La preghiamo di considerare questa comunicazione come nulla. In caso contrario, restiamo in attesa di riscontro.

Cordiali saluti
Showroom Ingenio`
  },
  ultimo_sollecito: {
    subject: "Ultimo sollecito pagamento – sospensione attività",
    body: `Gentile Cliente,

facendo seguito alle precedenti comunicazioni, ad oggi il pagamento relativo alla Sua pratica risulta ancora insoluto.

In assenza di riscontro entro breve termine, saremo costretti a sospendere le attività operative collegate all'intervento, come previsto dagli accordi sottoscritti.

La invitiamo pertanto a regolarizzare la posizione o a contattarci tempestivamente per evitare blocchi o rinvii.

In attesa di un Suo cortese riscontro.
Cordiali saluti
Showroom Ingenio`
  },
  richiesta_contatto: {
    subject: "Richiesta di contatto – pratica in gestione",
    body: `Gentile Cliente,

La contattiamo perché avremmo necessità di un confronto diretto in merito alla Sua pratica.

La invitiamo a:
• rispondere a questa email indicando un recapito telefonico
oppure
• contattarci direttamente negli orari di apertura

Un breve confronto ci permetterà di procedere in modo corretto e senza fraintendimenti.

Restiamo in attesa.
Cordiali saluti
Showroom Ingenio`
  }
};

export default function OrdiniPage({ user, logout }) {
  const { selectedYear } = useYear();
  const { mutate } = useSWRConfig();

  // SWR key: identifica univocamente la richiesta. La cache è persistente in
  // localStorage (vedi swrConfig.js) → apertura pagina istantanea anche dopo
  // chiusura/riapertura tab. Al focus del tab SWR revalida silente.
  const ordiniKey = `/ordini?year=${selectedYear}&light=true`;
  const fatturatoKey = `/payments/fatturato-mensile?year=${selectedYear}`;
  const commesseKey = `/commesse-pose?year=${selectedYear}&light=true`;

  const { data: ordini = [], isLoading: loadingOrdini } = useSWR(ordiniKey);
  const { data: fatturato = { mesi: {}, totale_anno: 0 } } = useSWR(fatturatoKey);
  const { data: commesseData = [] } = useSWR(commesseKey);

  // Map offer_id → commessa (derivato da commesse light)
  const commesseByOfferId = {};
  (commesseData || []).forEach((c) => {
    if (c.offer_id) commesseByOfferId[c.offer_id] = c;
  });

  // Loading: mostra skeleton SOLO se non abbiamo dati (né freschi né stale).
  const loading = loadingOrdini && ordini.length === 0;

  const refreshOrdini = () => mutate(ordiniKey);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [registroEmail, setRegistroEmail] = useState([]);
  const [loadingRegistro, setLoadingRegistro] = useState(false);
  const [docusignFlags, setDocusignFlags] = useState({});
  const [docusignData, setDocusignData] = useState({});
  const [sendingDocusign, setSendingDocusign] = useState(false);
  const [documentiNonNecessari, setDocumentiNonNecessari] = useState({});
  // Ordinamento Lista Ordini: 'recent' | 'oldest' | 'az' | 'za'
  const [sortBy, setSortBy] = useState("recent");
  // Intervista collegata all'ordine correntemente selezionato (per note/indirizzo/piano)
  const [interviewData, setInterviewData] = useState(null);
  // Documenti standard (template globali)
  const [documentiStandard, setDocumentiStandard] = useState([]);
  const [invioDocStandardId, setInvioDocStandardId] = useState(null);
  // Richiedi Documenti Personali
  const [reqDocPersonali, setReqDocPersonali] = useState({ codice_fiscale: false, documento_identita: false });
  const [invioReqDocPersonali, setInvioReqDocPersonali] = useState(false);
  // Documenti Extra (Certificazioni / Garanzia)
  const [docExtraTipo, setDocExtraTipo] = useState("certificazione");
  const [docExtraLabel, setDocExtraLabel] = useState("");
  const [docExtraFile, setDocExtraFile] = useState(null);
  const [docExtraUploading, setDocExtraUploading] = useState(false);
  const navigate = useNavigate();
  
  // Stato per nuovo nominativo
  const [showNuovoNominativo, setShowNuovoNominativo] = useState(false);
  const [nuovoNominativo, setNuovoNominativo] = useState({
    nome: "",
    cognome: "",
    email: "",
    telefono: "",
    via: "",
    citta: "",
    civico: "",
    piano: "",
    note: "",
    interview_id: null
  });
  const [savingNuovoNominativo, setSavingNuovoNominativo] = useState(false);
  const [interviews, setInterviews] = useState([]);
  // Ricerca intervista nel dialog "Nuovo Nominativo"
  const [interviewSearchTerm, setInterviewSearchTerm] = useState("");
  const [loadingInterviews, setLoadingInterviews] = useState(false);

  // Email Preview Modal state (riutilizzabile per Richiesta Documenti, Notifica Contrattualistica, Documenti Integrativi)
  const [emailPreview, setEmailPreview] = useState(null);
  // { open, title, endpoint, basePayload, onSent, successToast }

  const fetchInterviews = async () => {
    setLoadingInterviews(true);
    try {
      const res = await axios.get(`${API}/interviews`, { withCredentials: true });
      // Filtra solo interviste senza offerta già creata e con esito "vinto" o senza esito
      const available = res.data.filter(i => !i.offer_id);
      setInterviews(available);
    } catch (error) {
      console.error("Errore nel caricamento delle interviste:", error);
    } finally {
      setLoadingInterviews(false);
    }
  };

  const handleSelectInterview = (interviewId) => {
    if (!interviewId || interviewId === "manual") {
      // Reset form
      setNuovoNominativo({
        nome: "",
        cognome: "",
        email: "",
        telefono: "",
        via: "",
        citta: "",
        civico: "",
        piano: "",
        note: "",
        interview_id: null
      });
      return;
    }
    
    const interview = interviews.find(i => i.id === interviewId);
    if (interview) {
      setNuovoNominativo({
        nome: interview.nome || "",
        cognome: interview.cognome || "",
        email: interview.email || "",
        telefono: interview.telefono || "",
        via: interview.via || "",
        citta: interview.citta || "",
        civico: interview.civico || "",
        piano: interview.piano || "",
        note: interview.note || "",
        interview_id: interview.id
      });
    }
  };

  const fetchFatturato = () => mutate(fatturatoKey);

  // Fetch intervista collegata quando si apre la Gestione Ordine
  useEffect(() => {
    let cancelled = false;
    async function loadInterview() {
      if (!showDialog || !selectedOrdine?.interview_id) {
        setInterviewData(null);
        return;
      }
      try {
        const res = await axios.get(
          `${API}/interviews/${selectedOrdine.interview_id}`,
          { withCredentials: true }
        );
        if (!cancelled) setInterviewData(res.data);
      } catch (e) {
        if (!cancelled) setInterviewData(null);
      }
    }
    loadInterview();
    return () => { cancelled = true; };
  }, [showDialog, selectedOrdine?.interview_id]);

  // Carica lista documenti standard quando si apre il dialog
  useEffect(() => {
    if (!showDialog) {
      setReqDocPersonali({ codice_fiscale: false, documento_identita: false });
      return;
    }
    // Precompila i flag con la richiesta precedente se esiste
    if (selectedOrdine?.documenti_personali_richiesti) {
      setReqDocPersonali({
        codice_fiscale: !!selectedOrdine.documenti_personali_richiesti.codice_fiscale,
        documento_identita: !!selectedOrdine.documenti_personali_richiesti.documento_identita,
      });
    }
    axios.get(`${API}/documenti-standard`, { withCredentials: true })
      .then(r => setDocumentiStandard(r.data || []))
      .catch(() => setDocumentiStandard([]));
  }, [showDialog, selectedOrdine?.id]);

  const handleUploadDocExtra = async () => {
    if (!selectedOrdine || !docExtraFile) return;
    setDocExtraUploading(true);
    const fd = new FormData();
    fd.append("file", docExtraFile);
    fd.append("tipo", docExtraTipo);
    if (docExtraLabel) fd.append("label", docExtraLabel);
    try {
      await axios.post(
        `${API}/offers/${selectedOrdine.id}/upload-documento-extra`,
        fd,
        { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } }
      );
      toast.success("Documento caricato e disponibile nel Portale Cliente");
      const updated = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updated.data);
      setDocExtraFile(null);
      setDocExtraLabel("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento documento");
    } finally {
      setDocExtraUploading(false);
    }
  };

  const handleDeleteDocExtra = async (docId) => {
    if (!selectedOrdine) return;
    if (!window.confirm("Rimuovere questo documento? Il cliente non potrà più scaricarlo dal portale.")) return;
    try {
      await axios.delete(
        `${API}/offers/${selectedOrdine.id}/documento-extra/${docId}`,
        { withCredentials: true }
      );
      toast.success("Documento rimosso");
      const updated = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updated.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    }
  };

  const handleInviaRichiestaDocPersonali = async () => {
    if (!selectedOrdine) return;
    if (!reqDocPersonali.codice_fiscale && !reqDocPersonali.documento_identita) {
      toast.error("Seleziona almeno un documento da richiedere");
      return;
    }
    // Apre il Modal Preview: preview + editing + conferma invio in un'unica UI
    setEmailPreview({
      open: true,
      title: "Anteprima Email — Richiesta Documenti Personali",
      endpoint: `/ordini/${selectedOrdine.id}/richiedi-documenti-personali`,
      basePayload: {
        codice_fiscale: reqDocPersonali.codice_fiscale,
        documento_identita: reqDocPersonali.documento_identita,
      },
      successToast: "Richiesta documenti inviata al cliente",
      onSent: async () => {
        try {
          const updated = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
          setSelectedOrdine(updated.data);
        } catch (_) {}
      },
    });
  };

  const handleMarkDocPersonaleRicevuto = async (tipo, ricevuto) => {
    if (!selectedOrdine) return;
    try {
      await axios.put(
        `${API}/ordini/${selectedOrdine.id}/documenti-personali/mark-ricevuto`,
        { tipo, ricevuto },
        { withCredentials: true }
      );
      const updated = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updated.data);
      toast.success(ricevuto ? "Segnato come ricevuto" : "Flag rimosso");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const handleInviaDocumentoStandard = async (docId, docLabel) => {
    if (!selectedOrdine) return;
    if (!window.confirm(`Inviare "${docLabel}" al cliente?\n\nIl documento sarà disponibile nel Portale Cliente sotto "Contrattualistica da Firmare" e il cliente potrà scaricarlo, firmarlo e ricaricarlo.`)) {
      return;
    }
    setInvioDocStandardId(docId);
    try {
      const res = await axios.post(
        `${API}/ordini/${selectedOrdine.id}/invia-documento-standard/${docId}`,
        {},
        { withCredentials: true }
      );
      if (res.data?.success) {
        toast.success(`${docLabel} inviato al cliente`);
        fetchOrdini();
        const updated = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
        setSelectedOrdine(updated.data);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio documento");
    } finally {
      setInvioDocStandardId(null);
    }
  };

  // Renderer sottosezione "Documenti Standard" filtrata per sezione
  const renderDocumentiStandardSection = (sezioneKey) => {
    const docsSezione = documentiStandard.filter(d => (d.sezione || "generale") === sezioneKey);
    if (docsSezione.length === 0) return null;
    const now = Date.now();
    const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;
    return (
      <div className="mt-3 pt-3 border-t border-slate-200" data-testid={`doc-standard-sezione-${sezioneKey}`}>
        <div className="flex items-center gap-2 mb-2">
          <FileText className="w-4 h-4 text-indigo-600" />
          <h5 className="font-semibold text-slate-800 text-sm">Documenti Standard</h5>
          <span className="text-xs text-slate-500 italic">(uguali per tutti i contratti)</span>
        </div>
        <div className="space-y-2">
          {docsSezione.map(doc => {
            const docCaricato = (selectedOrdine?.documenti || []).find(d => d.tipo === doc.id && d.file_path);
            const giaInviato = !!docCaricato;
            const dataInvio = docCaricato?.data_caricamento;
            const inviatoDa = docCaricato?.inviato_da;
            const ricevuto = !!docCaricato?.ricevuto_firmato;
            const solecitoAt = docCaricato?.sollecito_doc_standard_sent_at;
            const giorniAttesa = dataInvio ? Math.floor((now - new Date(dataInvio).getTime()) / (24 * 60 * 60 * 1000)) : 0;
            const alertAttesa = giaInviato && !ricevuto && dataInvio && (now - new Date(dataInvio).getTime()) >= THREE_DAYS_MS;
            return (
              <div
                key={doc.id}
                className={`flex flex-col gap-3 p-3 border rounded-lg ${
                  ricevuto
                    ? "bg-emerald-50 border-emerald-200"
                    : alertAttesa
                      ? "bg-amber-50 border-amber-300"
                      : "bg-indigo-50 border-indigo-200"
                }`}
                data-testid={`doc-standard-${doc.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                  <div className="flex items-start gap-2 min-w-0 flex-1">
                    <FileText className={`w-5 h-5 shrink-0 mt-0.5 ${ricevuto ? "text-emerald-600" : alertAttesa ? "text-amber-600" : "text-indigo-600"}`} />
                    <div className="min-w-0">
                      <p className="font-medium text-slate-900 text-sm">{doc.label}</p>
                      {doc.descrizione && (
                        <p className="text-xs text-slate-600 mt-0.5">{doc.descrizione}</p>
                      )}
                      {giaInviato && (
                        <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1 flex-wrap">
                          <CheckCircle className="w-3 h-3" />
                          Inviato al cliente
                          {dataInvio && (
                            <span className="text-slate-600">
                              &nbsp;il {new Date(dataInvio).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                          {inviatoDa && (
                            <span className="text-slate-600">
                              &nbsp;da <strong className="text-slate-800">{inviatoDa}</strong>
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <a
                      href={`${API}/documenti-standard/${doc.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`preview-doc-standard-${doc.id}`}
                    >
                      <Button size="sm" variant="outline" className="text-xs">
                        <Download className="w-3 h-3 mr-1" />
                        Anteprima
                      </Button>
                    </a>
                    <Button
                      size="sm"
                      onClick={() => handleInviaDocumentoStandard(doc.id, doc.label)}
                      disabled={invioDocStandardId === doc.id || !doc.available}
                      data-testid={`invia-doc-standard-${doc.id}`}
                      className={`text-xs ${giaInviato ? "bg-emerald-600 hover:bg-emerald-700" : "bg-indigo-600 hover:bg-indigo-700"} text-white`}
                    >
                      <Upload className="w-3 h-3 mr-1" />
                      {invioDocStandardId === doc.id ? "Invio…" : giaInviato ? "Rinvia al Cliente" : "Invia al Cliente"}
                    </Button>
                  </div>
                </div>

                {giaInviato && (
                  <div className="flex flex-col gap-2 pl-7">
                    {alertAttesa && !ricevuto && (
                      <div
                        className="flex items-start gap-2 bg-amber-100 border border-amber-300 rounded-md px-3 py-2 text-xs text-amber-900"
                        data-testid={`doc-standard-alert-${doc.id}`}
                      >
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-amber-700" />
                        <div className="flex-1">
                          <p className="font-semibold">⚠️ In attesa del documento firmato da {giorniAttesa} giorni</p>
                          <p className="mt-0.5">
                            {solecitoAt
                              ? <>Sollecito automatico inviato il <strong>{new Date(solecitoAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>.</>
                              : <>Un sollecito automatico verrà inviato al cliente al prossimo controllo giornaliero.</>
                            }
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Recupera il registro email e i flag docusign quando si apre il dialogo
  useEffect(() => {
    if (selectedOrdine) {
      fetchRegistroEmail(selectedOrdine.id);
      setDocusignFlags(selectedOrdine.docusign_flags || {});
      setDocusignData(selectedOrdine.docusign_data || {});
      setDocumentiNonNecessari(selectedOrdine.documenti_non_necessari || {});
    }
  }, [selectedOrdine]);

  const fetchRegistroEmail = async (ordineId) => {
    setLoadingRegistro(true);
    try {
      const res = await axios.get(`${API}/ordini/${ordineId}/registro-email`, { withCredentials: true });
      setRegistroEmail(res.data.registro_email || []);
    } catch (error) {
      console.error("Errore nel caricamento del registro email:", error);
      setRegistroEmail([]);
    } finally {
      setLoadingRegistro(false);
    }
  };

  // fetchOrdini è ora gestita da SWR: usa refreshOrdini() per revalidare (mutate)
  const fetchOrdini = () => refreshOrdini();

  const handleVaiAllaPratica = (ordine) => {
    const commessa = commesseByOfferId[ordine.id];
    if (commessa && commessa.id) {
      navigate(`/commesse-pose/${commessa.id}`);
    } else {
      toast.info("Nessuna commessa di posa collegata a questo ordine");
    }
  };

  const handleToggleIgnoraDocCaricare = async (ordineId) => {
    try {
      const res = await axios.patch(
        `${API}/ordini/${ordineId}/ignora-doc-caricare`,
        {},
        { withCredentials: true }
      );
      const nuovo = res.data?.documenti_da_caricare_ignorato;
      // Aggiornamento ottimistico via SWR mutate: modifica la cache locale senza refetch
      mutate(ordiniKey, (current = []) =>
        current.map((o) => (o.id === ordineId ? { ...o, documenti_da_caricare_ignorato: nuovo } : o)),
        { revalidate: false }
      );
      toast.success(nuovo ? "Cliente ignorato dall'alert" : "Alert riattivato");
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleSaveNuovoNominativo = async () => {
    if (!nuovoNominativo.nome || !nuovoNominativo.cognome || !nuovoNominativo.telefono) {
      toast.error("Nome, Cognome e Telefono sono obbligatori");
      return;
    }
    
    setSavingNuovoNominativo(true);
    try {
      await axios.post(`${API}/ordini/nuovo`, nuovoNominativo, { withCredentials: true });
      toast.success("Nominativo aggiunto con successo");
      setShowNuovoNominativo(false);
      setNuovoNominativo({
        nome: "",
        cognome: "",
        email: "",
        telefono: "",
        via: "",
        citta: "",
        civico: "",
        piano: "",
        note: "",
        interview_id: null
      });
      fetchOrdini();
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      toast.error("Errore nel salvataggio del nominativo");
    } finally {
      setSavingNuovoNominativo(false);
    }
  };

  const handleDeleteOrdine = async (ordineId, nomeCompleto) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${nomeCompleto}" dalla Lista Contratti Attivi?\n\nQuesta azione eliminerà anche tutti i documenti e riferimenti collegati.`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/ordini/${ordineId}`, { withCredentials: true });
      toast.success("Nominativo eliminato con successo");
      fetchOrdini();
    } catch (error) {
      console.error("Errore nell'eliminazione:", error);
      toast.error("Errore nell'eliminazione del nominativo");
    }
  };

  const handleOpenDialog = async (ordine) => {
    // Apri subito con i dati leggeri (già caricati) per UX reattiva.
    setSelectedOrdine(ordine);
    setShowDialog(true);
    // In background, sostituisci con il documento full per popolare campi pesanti
    // (fatture, collaudo, documenti_extra, documenti_personali_*, condizioni_vendita).
    try {
      const res = await axios.get(`${API}/ordini/${ordine.id}`, { withCredentials: true });
      setSelectedOrdine(res.data);
    } catch (e) {
      console.error("Error loading full ordine:", e);
    }
  };

  const handleUploadDocument = async (tipo, file, ricevutoFirmato) => {
    if (!file) {
      toast.error("Seleziona un file");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tipo", tipo);
      formData.append("ricevuto_firmato", ricevutoFirmato);
      // Salta l'invio email automatico: lo faremo dopo l'anteprima
      formData.append("skip_email", "true");

      await axios.post(
        `${API}/ordini/${selectedOrdine.id}/upload-documento`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true,
        }
      );

      toast.success("Documento caricato. Anteprima email in preparazione...");
      fetchOrdini();
      const updatedOrdine = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);

      // Apri Modal Preview per l'email di notifica al cliente (solo per documenti contrattuali firmabili)
      if (!ricevutoFirmato && updatedOrdine.data?.email && updatedOrdine.data?.magic_link) {
        setEmailPreview({
          open: true,
          title: "Anteprima Email — Contrattualistica da firmare",
          endpoint: `/ordini/${selectedOrdine.id}/notifica-contrattualistica`,
          basePayload: {},
          successToast: "Email di notifica contrattualistica inviata al cliente",
          onSent: () => fetchRegistroEmail(selectedOrdine.id),
        });
      }
    } catch (error) {
      toast.error("Errore nel caricamento del documento");
    } finally {
      setUploading(false);
    }
  };

  const handleToggleFlag = async (tipo, currentValue) => {
    try {
      await axios.put(
        `${API}/ordini/${selectedOrdine.id}/documento/${tipo}/flag?ricevuto_firmato=${!currentValue}`,
        {},
        { withCredentials: true }
      );

      toast.success("Flag aggiornato");
      
      // Aggiorna l'ordine selezionato
      const updatedOrdine = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);
      fetchOrdini();
    } catch (error) {
      toast.error("Errore nell'aggiornamento del flag");
    }
  };

  const handleSaveInvioOrdinario = async (tipo, values) => {
    try {
      await axios.put(
        `${API}/ordini/${selectedOrdine.id}/documento/${tipo}/invio-ordinario`,
        values,
        { withCredentials: true }
      );
      toast.success("Invio ordinario aggiornato");
      const updatedOrdine = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);
      fetchOrdini();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Errore nell'aggiornamento");
    }
  };

  const handleBulkSignDocuments = async () => {
    if (!selectedOrdine) return;

    if (
      !window.confirm(
        "Confermi di segnare TUTTI i documenti come firmati e spegnere eventuali alert?"
      )
    )
      return;

    try {
      // Chiamata singola atomica con force_all=true: forza TUTTI i documenti presenti
      // sull'ordine, indipendentemente dallo stato attuale, e spegne gli alert
      const res = await axios.put(
        `${API}/ordini/${selectedOrdine.id}/documenti/bulk-flag`,
        {
          force_all: true,
          ricevuto_firmato: true,
        },
        { withCredentials: true }
      );

      const n = res.data?.updated_count ?? 0;
      if (n === 0) {
        toast.info("Nessun documento presente da segnare come firmato");
      } else {
        toast.success(`${n} ${n === 1 ? "documento segnato" : "documenti segnati"} come firmati. Alert spenti.`);
      }

      const updatedOrdine = await axios.get(
        `${API}/ordini/${selectedOrdine.id}`,
        { withCredentials: true }
      );
      setSelectedOrdine(updatedOrdine.data);
      fetchOrdini();
    } catch (error) {
      toast.error("Errore nell'aggiornamento in blocco");
    }
  };

  const handleDeleteDocument = async (tipo) => {
    if (!window.confirm("Sei sicuro di voler eliminare questo documento?")) return;

    try {
      await axios.delete(`${API}/ordini/${selectedOrdine.id}/documento/${tipo}`, { withCredentials: true });
      toast.success("Documento eliminato");
      
      // Aggiorna l'ordine selezionato
      const updatedOrdine = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);
      fetchOrdini();
    } catch (error) {
      toast.error("Errore nell'eliminazione del documento");
    }
  };

  const copyEmailToClipboard = (templateKey) => {
    const template = EMAIL_TEMPLATES[templateKey];
    const emailText = `Oggetto: ${template.subject}\n\n${template.body}`;
    
    navigator.clipboard.writeText(emailText).then(() => {
      toast.success("Email copiata negli appunti!");
    }).catch(() => {
      toast.error("Errore nella copia");
    });
  };

  const handleUploadFatturaCollaudo = async (ordineId, file, tipo) => {
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("tipo", tipo);
      // Salta l'invio email automatico: lo faremo dopo l'anteprima
      formData.append("skip_email", "true");
      
      await axios.post(
        `${API}/ordini/${ordineId}/upload-fatture-collaudo`,
        formData,
        { withCredentials: true }
      );
      
      toast.success(`${tipo === "fattura" ? "Fattura" : "Collaudo"} caricato. Anteprima email in preparazione...`);
      
      await fetchOrdini();
      const updatedOrdine = await axios.get(`${API}/ordini/${ordineId}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);
      fetchRegistroEmail(ordineId);

      // Apri Modal Preview per notifica documenti integrativi
      if (updatedOrdine.data?.email && updatedOrdine.data?.magic_link) {
        setEmailPreview({
          open: true,
          title: `Anteprima Email — Documenti Integrativi (${tipo === "fattura" ? "Fatture" : "Collaudo"})`,
          endpoint: `/ordini/${ordineId}/notifica-documenti-integrativi`,
          basePayload: { tipo },
          successToast: "Email documenti integrativi inviata al cliente",
          onSent: () => fetchRegistroEmail(ordineId),
        });
      }
    } catch (error) {
      console.error("Error uploading:", error);
      toast.error("Errore nel caricamento del documento");
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFatturaCollaudo = async (ordineId, tipo, index) => {
    try {
      await axios.delete(
        `${API}/ordini/${ordineId}/fatture-collaudo/${tipo}/${index}`,
        { withCredentials: true }
      );
      
      toast.success(`${tipo === "fattura" ? "Fattura" : "Collaudo"} eliminato`);
      
      // Ricarica gli ordini (lista light) e il dettaglio dell'ordine corrente (full)
      await fetchOrdini();
      const updatedOrdine = await axios.get(`${API}/ordini/${ordineId}`, { withCredentials: true });
      setSelectedOrdine(updatedOrdine.data);
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Errore nell'eliminazione del documento");
    }
  };

  const copyRegistroEmailToClipboard = (email) => {
    const emailText = `Oggetto: ${email.oggetto}\n\n${email.corpo}`;
    
    navigator.clipboard.writeText(emailText).then(() => {
      toast.success("Testo email copiato negli appunti!");
    }).catch(() => {
      toast.error("Errore nella copia");
    });
  };

  const handleDocusignToggle = async (tipo, attivo) => {
    if (!selectedOrdine) return;
    
    setSendingDocusign(true);
    try {
      const formData = new FormData();
      formData.append("tipo", tipo);
      formData.append("attivo", attivo);
      
      // Mantieni i dati esistenti se presenti
      const currentData = docusignData[tipo] || {};
      if (currentData.data_invio) {
        formData.append("data_invio", currentData.data_invio);
      }
      if (currentData.ricevuto) {
        formData.append("ricevuto", currentData.ricevuto);
      }
      
      const response = await axios.post(
        `${API}/ordini/${selectedOrdine.id}/docusign-flag`,
        formData,
        { withCredentials: true }
      );
      
      // Aggiorna lo stato locale
      setDocusignFlags(prev => ({ ...prev, [tipo]: attivo }));
      setDocusignData(prev => ({
        ...prev,
        [tipo]: {
          data_invio: response.data.data_invio,
          ricevuto: response.data.ricevuto || false
        }
      }));
      
      // Se si attiva Docusign, attiva automaticamente anche "Non Necessario"
      if (attivo) {
        const nonNecFormData = new FormData();
        nonNecFormData.append("tipo", tipo);
        nonNecFormData.append("non_necessario", true);
        
        await axios.post(
          `${API}/ordini/${selectedOrdine.id}/documento-non-necessario`,
          nonNecFormData,
          { withCredentials: true }
        );
        
        setDocumentiNonNecessari(prev => ({ ...prev, [tipo]: true }));
      }
      
      // Ricarica l'ordine per avere i dati aggiornati (lista light + dettaglio full)
      await fetchOrdini();
      const detail = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(detail.data);
      
      // Ricarica il registro email
      fetchRegistroEmail(selectedOrdine.id);
      
      if (attivo) {
        toast.success("Email di notifica Docusign inviata al cliente");
      } else {
        toast.success("Flag Docusign rimosso");
      }
    } catch (error) {
      console.error("Errore nell'aggiornamento flag Docusign:", error);
      toast.error("Errore nell'aggiornamento del flag");
    } finally {
      setSendingDocusign(false);
    }
  };

  const handleDocusignDataChange = async (tipo, field, value) => {
    if (!selectedOrdine) return;
    
    try {
      const formData = new FormData();
      formData.append("tipo", tipo);
      formData.append("attivo", docusignFlags[tipo] || false);
      
      const currentData = docusignData[tipo] || {};
      
      if (field === 'data_invio') {
        formData.append("data_invio", value);
        formData.append("ricevuto", currentData.ricevuto || false);
      } else if (field === 'ricevuto') {
        formData.append("ricevuto", value);
        if (currentData.data_invio) {
          formData.append("data_invio", currentData.data_invio);
        }
      }
      
      const response = await axios.post(
        `${API}/ordini/${selectedOrdine.id}/docusign-flag`,
        formData,
        { withCredentials: true }
      );
      
      // Aggiorna lo stato locale
      setDocusignData(prev => ({
        ...prev,
        [tipo]: {
          data_invio: response.data.data_invio,
          ricevuto: response.data.ricevuto || false
        }
      }));
      
      // Ricarica l'ordine (lista light + dettaglio full)
      await fetchOrdini();
      const detail = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(detail.data);
      
      toast.success("Dati Docusign aggiornati");
    } catch (error) {
      console.error("Errore nell'aggiornamento dati Docusign:", error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const handleNonNecessarioToggle = async (tipo, nonNecessario) => {
    if (!selectedOrdine) return;
    
    try {
      const formData = new FormData();
      formData.append("tipo", tipo);
      formData.append("non_necessario", nonNecessario);
      
      await axios.post(
        `${API}/ordini/${selectedOrdine.id}/documento-non-necessario`,
        formData,
        { withCredentials: true }
      );
      
      // Aggiorna lo stato locale
      setDocumentiNonNecessari(prev => ({ ...prev, [tipo]: nonNecessario }));
      
      // Ricarica l'ordine per avere i dati aggiornati (lista light + dettaglio full)
      await fetchOrdini();
      const detail = await axios.get(`${API}/ordini/${selectedOrdine.id}`, { withCredentials: true });
      setSelectedOrdine(detail.data);
      
      toast.success(nonNecessario ? "Documento segnato come non necessario" : "Flag rimosso");
    } catch (error) {
      console.error("Errore nell'aggiornamento flag non necessario:", error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const getDocumento = (documenti, tipo) => {
    if (!documenti) return null;
    return documenti.find(d => d.tipo === tipo);
  };

  const getStatoFirmeEletroniche = (ordine) => {
    const docusignFlags = ordine.docusign_flags || {};
    const docusignData = ordine.docusign_data || {};
    
    const hasCondizioni = docusignFlags.condizioni_contrattuali;
    const hasNorme = docusignFlags.norme_uni;
    
    if (!hasCondizioni && !hasNorme) {
      return { stato: "Non Previsto", color: "bg-slate-100 text-slate-700" };
    }
    
    const condizioniRicevuto = docusignData.condizioni_contrattuali?.ricevuto;
    const normeRicevuto = docusignData.norme_uni?.ricevuto;
    
    if ((hasCondizioni && condizioniRicevuto) || (hasNorme && normeRicevuto)) {
      return { stato: "Ricevuto", color: "bg-green-100 text-green-700" };
    }
    
    return { stato: "Inviato", color: "bg-blue-100 text-blue-700" };
  };

  const getStatoFirmeAutografe = (ordine) => {
    const documenti = ordine.documenti || [];
    
    // Controlla i documenti principali delle tab (3 slot post_esecutivo + condizioni + norme)
    const tipiDocumenti = ['post_esecutivo', 'post_esecutivo_2', 'post_esecutivo_3', 'condizioni_contrattuali', 'norme_uni'];
    const documentiTab = documenti.filter(d => tipiDocumenti.includes(d.tipo));
    
    // Non Inviato: nessun documento delle tab è stato caricato
    if (documentiTab.length === 0) {
      return { stato: "Non Inviato", color: "bg-slate-100 text-slate-700" };
    }
    
    // Ricevuto: tutti i documenti caricati hanno ricevuto_firmato: true
    const tuttiRicevutiFirmati = documentiTab.every(d => d.ricevuto_firmato === true);
    
    if (tuttiRicevutiFirmati) {
      return { stato: "Ricevuto", color: "bg-green-100 text-green-700" };
    }
    
    // Inviato: almeno un documento è stato caricato ma non tutti sono ricevuti firmati
    return { stato: "Inviato", color: "bg-orange-100 text-orange-700" };
  };

  const filteredOrdini = ordini
    .filter(ordine =>
      `${ordine.nome} ${ordine.cognome} ${ordine.email}`.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === "az" || sortBy === "za") {
        const nomeA = `${a.cognome || ""} ${a.nome || ""}`.trim().toLowerCase();
        const nomeB = `${b.cognome || ""} ${b.nome || ""}`.trim().toLowerCase();
        const cmp = nomeA.localeCompare(nomeB, "it");
        return sortBy === "az" ? cmp : -cmp;
      }
      // cronologico (usa created_at o data_creazione, fallback stringa vuota)
      const dateA = a.created_at || a.data_creazione || "";
      const dateB = b.created_at || b.data_creazione || "";
      return sortBy === "recent"
        ? dateB.localeCompare(dateA)
        : dateA.localeCompare(dateB);
    });

  if (loading) {
    return (
      <PageLayout user={user} logout={logout} testId="ordini-page" className="overflow-y-auto">
        <div className="p-6 max-w-7xl mx-auto">
          <div className="mb-6">
            <div className="h-8 bg-slate-200 rounded w-64 animate-pulse mb-2" />
            <div className="h-4 bg-slate-100 rounded w-96 animate-pulse" />
          </div>
          <SkeletonTable rows={8} cols={5} />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout user={user} logout={logout} testId="ordini-page" className="overflow-y-auto">
        <div className="p-4 md:p-6 lg:p-8">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-800 mb-2">📦 Contratti</h1>
            <p className="text-sm md:text-base text-slate-600">Gestione ordini da offerte accettate</p>
          </div>

          {/* Search */}
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca per nome..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="flex-1"
                />
              </div>
            </CardContent>
          </Card>

          {/* Situazione Solleciti da Fare */}
          <DocumentiFirmatiDaCaricare />

          {/* Documenti Personali Mancanti */}
          <DocumentiPersonaliMancanti />

          {/* Ordini Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Totale Ordini</p>
                    <p className="text-xl font-bold text-slate-800">{ordini.length}</p>
                  </div>
                  <FileText className="w-6 h-6 text-blue-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">Completi</p>
                    <p className="text-xl font-bold text-green-600">
                      {ordini.filter(o => {
                        const docs = o.documenti || [];
                        return docs.length >= 3 && docs.every(d => d.ricevuto_firmato);
                      }).length}
                    </p>
                  </div>
                  <CheckCircle className="w-6 h-6 text-green-600" />
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-slate-600">In Attesa</p>
                    <p className="text-xl font-bold text-orange-600">
                      {ordini.filter(o => {
                        const docs = o.documenti || [];
                        return docs.length < 3 || !docs.every(d => d.ricevuto_firmato);
                      }).length}
                    </p>
                  </div>
                  <XCircle className="w-6 h-6 text-orange-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sezione Fatturato Mensile */}
          <Card className="mb-6">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-600" />
                Fatturato {selectedYear}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2 mb-4">
                {Object.entries(fatturato.mesi || {}).map(([key, data]) => (
                  <div key={key} className="text-center p-2 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">{data.nome?.substring(0, 3)}</p>
                    <p className={`text-sm font-bold ${data.totale > 0 ? 'text-emerald-600' : 'text-slate-400'}`}>
                      {data.totale > 0 ? `€${(data.totale / 1000).toFixed(1)}k` : '-'}
                    </p>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between pt-3 border-t">
                <span className="text-sm text-slate-600">Totale Anno</span>
                <span className="text-xl font-bold text-emerald-600">
                  €{(fatturato.totale_anno || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Warning Documenti da Caricare */}
          {(() => {
            const tipiDocumento = ["post_esecutivo", "condizioni_contrattuali", "norme_uni"];
            const labelDocumento = {
              "post_esecutivo": "Post Esecutivo",
              "condizioni_contrattuali": "Condizioni Contrattuali",
              "norme_uni": "Norme UNI"
            };

            // Un tipo è "soddisfatto" (non va nel warning) se:
            // - è marcato come non necessario, OR
            // - è gestito via Docusign (flag attivo, anche se non ancora ricevuto: andrà nel warning "in attesa"), OR
            // - esiste un documento con file_path caricato, OR
            // - esiste un documento già flaggato come ricevuto_firmato (anche senza file_path)
            const isTipoSoddisfatto = (ordine, tipo) => {
              const nonNecessari = ordine.documenti_non_necessari || {};
              if (nonNecessari[tipo]) return true;
              const docusignFlags = ordine.docusign_flags || {};
              if (docusignFlags[tipo]) return true; // gestito via docusign → fuori da "da caricare"
              const docs = ordine.documenti || [];
              const docTipo = docs.find(d => d.tipo === tipo);
              if (!docTipo) return false;
              if (docTipo.file_path) return true;
              if (docTipo.ricevuto_firmato === true) return true;
              return false;
            };

            const clienteKey = (o) => (o.email || "").trim().toLowerCase() || `${(o.nome || "").trim().toLowerCase()}|${(o.cognome || "").trim().toLowerCase()}`;
            const latestByCliente = {};
            ordini.forEach(ordine => {
              // Escludi ordini per cui l'utente ha marcato "Ignora"
              if (ordine.documenti_da_caricare_ignorato) return;
              if (!tipiDocumento.some(tipo => !isTipoSoddisfatto(ordine, tipo))) return;
              const k = clienteKey(ordine);
              const prev = latestByCliente[k];
              if (!prev || (ordine.created_at || "") > (prev.created_at || "")) latestByCliente[k] = ordine;
            });

            const clientiConDocumentiMancanti = Object.values(latestByCliente).map(ordine => {
              const documentiMancanti = tipiDocumento
                .filter(tipo => !isTipoSoddisfatto(ordine, tipo))
                .map(tipo => labelDocumento[tipo]);
              const numSoddisfatti = tipiDocumento.length - documentiMancanti.length;

              return {
                id: ordine.id,
                nome: `${ordine.nome || ''} ${ordine.cognome || ''}`.trim(),
                telefono: ordine.telefono,
                documentiMancanti,
                numCaricati: numSoddisfatti,
                hasCommessa: !!commesseByOfferId[ordine.id],
              };
            }).filter(cliente => cliente.documentiMancanti.length > 0);

            if (clientiConDocumentiMancanti.length === 0) return null;

            return (
              <Card className="border-red-300 bg-red-50 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-red-700">
                    <Upload className="w-5 h-5 animate-pulse" />
                    Documenti da Caricare ({clientiConDocumentiMancanti.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-red-600 mb-3">
                    I seguenti clienti hanno documenti mancanti da caricare per la firma
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {clientiConDocumentiMancanti.map(cliente => (
                      <div 
                        key={cliente.id} 
                        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-white rounded-lg border border-red-200"
                        data-testid={`doc-caricare-row-${cliente.id}`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <FileText className="w-4 h-4 text-red-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 truncate">{cliente.nome}</p>
                            <p className="text-xs text-red-600 truncate">
                              Mancano: {cliente.documentiMancanti.join(", ")}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" title="Nascondi questo cliente dall'alert">
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-red-600 cursor-pointer"
                              checked={false}
                              onChange={() => handleToggleIgnoraDocCaricare(cliente.id)}
                              data-testid={`ignora-doc-caricare-${cliente.id}`}
                            />
                            Ignora
                          </label>
                          {cliente.hasCommessa && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                const ordine = ordini.find(o => o.id === cliente.id);
                                if (ordine) handleVaiAllaPratica(ordine);
                              }}
                              data-testid={`vai-alla-pratica-${cliente.id}`}
                              className="text-blue-700 border-blue-300 hover:bg-blue-50 text-xs"
                              title="Apri Commesse e Pose / Dettaglio Commessa"
                            >
                              Vai alla pratica
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const ordine = ordini.find(o => o.id === cliente.id);
                              if (ordine) {
                                setSelectedOrdine(ordine);
                                setShowDialog(true);
                              }
                            }}
                            className="text-red-700 border-red-300 hover:bg-red-100 text-xs"
                          >
                            Carica Documenti
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Warning Documenti in Attesa */}
          {(() => {
            // Calcola clienti con documenti in attesa (sia autografi che elettronici)
            const tipiDocumento = ["post_esecutivo", "condizioni_contrattuali", "norme_uni"];
            const labelDocumento = {
              "post_esecutivo": "Post Esecutivo",
              "condizioni_contrattuali": "Condizioni Contrattuali (Autografo)",
              "norme_uni": "Norme UNI (Autografo)"
            };
            const labelDocusign = {
              "condizioni_contrattuali": "Condizioni Contrattuali (Docusign)",
              "norme_uni": "Norme UNI (Docusign)"
            };
            
            const clientiInAttesa = ordini.filter(ordine => {
              const docs = ordine.documenti || [];
              const nonNecessari = ordine.documenti_non_necessari || {};
              const docusignFlags = ordine.docusign_flags || {};
              const docusignData = ordine.docusign_data || {};
              
              // Controlla documenti autografi in attesa
              const hasAutografiInAttesa = docs.some(d => 
                tipiDocumento.includes(d.tipo) && 
                !nonNecessari[d.tipo] &&
                !docusignFlags[d.tipo] && // Non gestito via Docusign
                d.file_path && 
                !d.ricevuto_firmato
              );
              
              // Controlla documenti Docusign inviati ma non ricevuti
              const hasDocusignInAttesa = ["condizioni_contrattuali", "norme_uni"].some(tipo =>
                docusignFlags[tipo] && !docusignData[tipo]?.ricevuto
              );
              
              return hasAutografiInAttesa || hasDocusignInAttesa;
            }).map(ordine => {
              const docs = ordine.documenti || [];
              const nonNecessari = ordine.documenti_non_necessari || {};
              const docusignFlags = ordine.docusign_flags || {};
              const docusignData = ordine.docusign_data || {};
              
              // Documenti autografi in attesa (escludi quelli gestiti via Docusign)
              const autografiInAttesa = docs
                .filter(d => 
                  tipiDocumento.includes(d.tipo) && 
                  !nonNecessari[d.tipo] && 
                  !docusignFlags[d.tipo] &&
                  d.file_path && 
                  !d.ricevuto_firmato
                )
                .map(d => labelDocumento[d.tipo] || d.tipo);
              
              // Documenti Docusign inviati ma non ricevuti
              const docusignInAttesa = ["condizioni_contrattuali", "norme_uni"]
                .filter(tipo => docusignFlags[tipo] && !docusignData[tipo]?.ricevuto)
                .map(tipo => labelDocusign[tipo]);
              
              const documentiInAttesa = [...autografiInAttesa, ...docusignInAttesa];
              
              return {
                id: ordine.id,
                nome: `${ordine.nome || ''} ${ordine.cognome || ''}`.trim(),
                telefono: ordine.telefono,
                documentiInAttesa
              };
            }).filter(cliente => cliente.documentiInAttesa.length > 0);

            if (clientiInAttesa.length === 0) return null;

            return (
              <Card className="border-amber-300 bg-amber-50 mb-6">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5 animate-pulse" />
                    Documenti in Attesa di Firma ({clientiInAttesa.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-amber-600 mb-3">
                    I seguenti clienti hanno documenti caricati ma non ancora restituiti firmati
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {clientiInAttesa.map(cliente => (
                      <div 
                        key={cliente.id} 
                        className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200"
                      >
                        <div className="flex items-center gap-3">
                          <Clock className="w-4 h-4 text-amber-500" />
                          <div>
                            <p className="font-medium text-slate-900">{cliente.nome}</p>
                            <p className="text-xs text-amber-600">
                              In attesa: {cliente.documentiInAttesa.join(", ")}
                            </p>
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            const ordine = ordini.find(o => o.id === cliente.id);
                            if (ordine) {
                              setSelectedOrdine(ordine);
                              setShowDialog(true);
                            }
                          }}
                          className="text-amber-700 border-amber-300 hover:bg-amber-100"
                        >
                          Carica Documenti
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          {/* Ordini List */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-col gap-3">
                <div className="flex justify-between items-center flex-wrap gap-2">
                  <CardTitle className="text-lg">Lista Contratti Attivi</CardTitle>
                  <Button 
                    onClick={() => {
                      setShowNuovoNominativo(true);
                      fetchInterviews();
                    }}
                    className="flex items-center gap-2"
                    size="sm"
                    data-testid="nuovo-nominativo-btn"
                  >
                    <Plus className="w-4 h-4" />
                    Nuovo Nominativo
                  </Button>
                </div>
                {/* Toolbar: ricerca + ordinamento */}
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                  <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <Input
                      placeholder="Cerca cliente per nome, cognome o email…"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-9"
                      data-testid="lista-ordini-search"
                    />
                  </div>
                  <div className="flex flex-wrap gap-1" data-testid="lista-ordini-sort">
                    <Button
                      size="sm"
                      variant={sortBy === "recent" ? "default" : "outline"}
                      onClick={() => setSortBy("recent")}
                      className="text-xs"
                      data-testid="sort-recent"
                      title="Più recenti prima"
                    >
                      <ArrowDownNarrowWide className="w-3.5 h-3.5 mr-1" />
                      Recenti
                    </Button>
                    <Button
                      size="sm"
                      variant={sortBy === "oldest" ? "default" : "outline"}
                      onClick={() => setSortBy("oldest")}
                      className="text-xs"
                      data-testid="sort-oldest"
                      title="Più vecchi prima"
                    >
                      <ArrowUpNarrowWide className="w-3.5 h-3.5 mr-1" />
                      Vecchi
                    </Button>
                    <Button
                      size="sm"
                      variant={sortBy === "az" ? "default" : "outline"}
                      onClick={() => setSortBy("az")}
                      className="text-xs"
                      data-testid="sort-az"
                      title="Alfabetico A→Z (per cognome)"
                    >
                      <ArrowDownAZ className="w-3.5 h-3.5 mr-1" />
                      A-Z
                    </Button>
                    <Button
                      size="sm"
                      variant={sortBy === "za" ? "default" : "outline"}
                      onClick={() => setSortBy("za")}
                      className="text-xs"
                      data-testid="sort-za"
                      title="Alfabetico Z→A (per cognome)"
                    >
                      <ArrowUpAZ className="w-3.5 h-3.5 mr-1" />
                      Z-A
                    </Button>
                  </div>
                </div>
                {(searchTerm || sortBy !== "recent") && (
                  <p className="text-xs text-slate-500" data-testid="lista-ordini-count">
                    {filteredOrdini.length} risultati • ordinati per {
                      sortBy === "recent" ? "data (più recenti)" :
                      sortBy === "oldest" ? "data (più vecchi)" :
                      sortBy === "az" ? "cognome A→Z" : "cognome Z→A"
                    }
                  </p>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredOrdini.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 text-sm">Nessun ordine trovato</p>
                </div>
              ) : (
                <div className="overflow-auto max-h-[calc(100vh-500px)] -mx-4 md:mx-0">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-white z-10">
                      <tr className="border-b">
                        <th className="text-left p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 bg-white">Cliente</th>
                        <th className="text-center p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 bg-white">Firme Elettroniche</th>
                        <th className="text-center p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 bg-white">Firme Autografe</th>
                        <th className="text-center p-2 md:p-3 text-xs md:text-sm font-semibold text-slate-700 bg-white">Azioni</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrdini.map((ordine) => {
                        const statoElettronica = getStatoFirmeEletroniche(ordine);
                        const statoAutografa = getStatoFirmeAutografe(ordine);
                        
                        return (
                          <tr key={ordine.id} className="border-b hover:bg-slate-50">
                            <td className="p-2 md:p-3">
                              <div>
                                <span className="font-medium text-sm md:text-base block">{ordine.nome} {ordine.cognome}</span>
                                <span className="text-xs text-slate-500">{ordine.email}</span>
                              </div>
                            </td>
                            <td className="p-2 md:p-3">
                              <div className="flex justify-center">
                                <span className={`px-3 py-1 text-xs rounded-full font-medium ${statoElettronica.color}`}>
                                  {statoElettronica.stato}
                                </span>
                              </div>
                            </td>
                            <td className="p-2 md:p-3">
                              <div className="flex justify-center">
                                <span className={`px-3 py-1 text-xs rounded-full font-medium ${statoAutografa.color}`}>
                                  {statoAutografa.stato}
                                </span>
                              </div>
                            </td>
                            <td className="p-2 md:p-3">
                              <div className="flex justify-center gap-2">
                                <Button
                                  size="sm"
                                  onClick={() => handleOpenDialog(ordine)}
                                  className="text-xs px-3 py-1"
                                >
                                  Gestisci
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => handleDeleteOrdine(ordine.id, `${ordine.nome} ${ordine.cognome}`)}
                                  className="text-xs px-2 py-1"
                                  title="Elimina nominativo"
                                  data-testid={`delete-ordine-${ordine.id}`}
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

      {/* Dialog Nuovo Nominativo */}
      <Dialog open={showNuovoNominativo} onOpenChange={setShowNuovoNominativo}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Nominativo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Select per prelevare da intervista */}
            <div>
              <Label htmlFor="select-interview">Preleva da Intervista (opzionale)</Label>
              <Input
                type="text"
                placeholder="Cerca per cognome, nome, codice o telefono..."
                value={interviewSearchTerm}
                onChange={(e) => setInterviewSearchTerm(e.target.value)}
                className="mb-2"
                data-testid="search-interview-input"
              />
              {(() => {
                const filteredSortedInterviews = interviews
                  .filter((i) => {
                    if (!interviewSearchTerm.trim()) return true;
                    const q = interviewSearchTerm.toLowerCase();
                    return `${i.cognome || ""} ${i.nome || ""} ${i.codice || ""} ${i.telefono || ""}`.toLowerCase().includes(q);
                  })
                  .sort((a, b) => (a.cognome || "").localeCompare(b.cognome || "", "it", { sensitivity: "base" }));
                return (
                  <Select
                    value={nuovoNominativo.interview_id || "manual"}
                    onValueChange={(value) => { handleSelectInterview(value === "manual" ? null : value); setInterviewSearchTerm(""); }}
                  >
                    <SelectTrigger className="w-full" data-testid="select-interview">
                      <SelectValue placeholder={loadingInterviews ? "Caricamento..." : `Seleziona un'intervista... (${filteredSortedInterviews.length} risultati, ordinati per cognome)`} />
                    </SelectTrigger>
                    <SelectContent className="max-h-72">
                      <SelectItem value="manual">-- Inserisci manualmente --</SelectItem>
                      {filteredSortedInterviews.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-slate-500 text-center">
                          Nessun cliente trovato
                        </div>
                      ) : (
                        filteredSortedInterviews.map((interview) => (
                          <SelectItem key={interview.id} value={interview.id}>
                            {interview.cognome} {interview.nome}{interview.codice ? ` — ${interview.codice}` : ""} — {interview.telefono || interview.email || "N/A"}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                );
              })()}
              {interviews.length === 0 && !loadingInterviews && (
                <p className="text-xs text-slate-500 mt-1">Nessuna intervista disponibile</p>
              )}
            </div>
            
            <div className="border-t pt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="nuovo-nome">Nome *</Label>
                  <Input
                    id="nuovo-nome"
                    value={nuovoNominativo.nome}
                    onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, nome: e.target.value })}
                    placeholder="Nome"
                    data-testid="nuovo-nome-input"
                  />
                </div>
                <div>
                  <Label htmlFor="nuovo-cognome">Cognome *</Label>
                  <Input
                    id="nuovo-cognome"
                    value={nuovoNominativo.cognome}
                    onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, cognome: e.target.value })}
                    placeholder="Cognome"
                    data-testid="nuovo-cognome-input"
                  />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nuovo-email">Email</Label>
                <Input
                  id="nuovo-email"
                  type="email"
                  value={nuovoNominativo.email}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, email: e.target.value })}
                  placeholder="email@esempio.com"
                  data-testid="nuovo-email-input"
                />
              </div>
              <div>
                <Label htmlFor="nuovo-telefono">Telefono *</Label>
                <Input
                  id="nuovo-telefono"
                  value={nuovoNominativo.telefono}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, telefono: e.target.value })}
                  placeholder="Numero telefono"
                  data-testid="nuovo-telefono-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-2">
                <Label htmlFor="nuovo-via">Via</Label>
                <Input
                  id="nuovo-via"
                  value={nuovoNominativo.via}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, via: e.target.value })}
                  placeholder="Via/Piazza"
                  data-testid="nuovo-via-input"
                />
              </div>
              <div>
                <Label htmlFor="nuovo-civico">Civico</Label>
                <Input
                  id="nuovo-civico"
                  value={nuovoNominativo.civico}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, civico: e.target.value })}
                  placeholder="N°"
                  data-testid="nuovo-civico-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nuovo-citta">Città</Label>
                <Input
                  id="nuovo-citta"
                  value={nuovoNominativo.citta}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, citta: e.target.value })}
                  placeholder="Città"
                  data-testid="nuovo-citta-input"
                />
              </div>
              <div>
                <Label htmlFor="nuovo-piano">Piano</Label>
                <Input
                  id="nuovo-piano"
                  value={nuovoNominativo.piano}
                  onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, piano: e.target.value })}
                  placeholder="Piano"
                  data-testid="nuovo-piano-input"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="nuovo-note">Note</Label>
              <Textarea
                id="nuovo-note"
                value={nuovoNominativo.note}
                onChange={(e) => setNuovoNominativo({ ...nuovoNominativo, note: e.target.value })}
                placeholder="Note aggiuntive..."
                rows={3}
                data-testid="nuovo-note-input"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => setShowNuovoNominativo(false)}
              >
                Annulla
              </Button>
              <Button 
                onClick={handleSaveNuovoNominativo}
                disabled={savingNuovoNominativo}
                data-testid="salva-nuovo-nominativo-btn"
              >
                {savingNuovoNominativo ? "Salvataggio..." : "Salva Nominativo"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Gestione Ordine */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Gestione Ordine - {selectedOrdine?.nome} {selectedOrdine?.cognome}
            </DialogTitle>
          </DialogHeader>

          {selectedOrdine && (
            <div className="space-y-6">
              {/* Info Cliente */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Informazioni Cliente</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Nome</span>
                      <p className="font-medium text-slate-900" data-testid="ordine-info-nome">{selectedOrdine.nome || "—"}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Cognome</span>
                      <p className="font-medium text-slate-900" data-testid="ordine-info-cognome">{selectedOrdine.cognome || "—"}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Email</span>
                      <p className="font-medium text-slate-900 break-all" data-testid="ordine-info-email">{selectedOrdine.email || "—"}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Telefono</span>
                      <p className="font-medium text-slate-900" data-testid="ordine-info-telefono">{selectedOrdine.telefono || "—"}</p>
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Indirizzo</span>
                      <p className="font-medium text-slate-900" data-testid="ordine-info-indirizzo">
                        {(() => {
                          const via = interviewData?.via || "";
                          const civico = interviewData?.civico || "";
                          const cap = interviewData?.cap || "";
                          const citta = interviewData?.citta || "";
                          const provincia = interviewData?.provincia || "";
                          const parts = [];
                          if (via || civico) parts.push(`${via}${civico ? ` ${civico}` : ""}`);
                          const cittaLine = [cap, citta, provincia ? `(${provincia})` : ""].filter(Boolean).join(" ");
                          if (cittaLine) parts.push(cittaLine);
                          return parts.length > 0 ? parts.join(" – ") : "—";
                        })()}
                      </p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs uppercase tracking-wide">Piano</span>
                      <p className="font-medium text-slate-900" data-testid="ordine-info-piano">
                        {interviewData?.piano || "—"}
                      </p>
                    </div>
                  </div>

                  {/* Box Note Intervista */}
                  <div className="mt-5 pt-4 border-t border-slate-200">
                    <span className="text-slate-500 text-xs uppercase tracking-wide">Note Intervista</span>
                    <div
                      className="mt-1 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-slate-800 whitespace-pre-wrap min-h-[3rem]"
                      data-testid="ordine-info-note-intervista"
                    >
                      {interviewData?.note ? interviewData.note : <span className="text-slate-400 italic">Nessuna nota inserita in fase di creazione intervista</span>}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Documenti Ordine - subito dopo Informazioni Cliente */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <CardTitle className="text-lg">Documenti Ordine</CardTitle>
                    <button
                      type="button"
                      onClick={handleBulkSignDocuments}
                      data-testid="bulk-sign-documents-btn"
                      className="px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700"
                      title="Forza TUTTI i documenti come ricevuti firmati e spegne gli alert"
                    >
                      ✓ Segna tutti Firmati (forza)
                    </button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Post Esecutivo (slot 1 - obbligatorio) */}
                  <DocumentSection
                    tipo="post_esecutivo"
                    label="Post Esecutivo"
                    ordineId={selectedOrdine.id}
                    documento={getDocumento(selectedOrdine.documenti, "post_esecutivo")}
                    onUpload={handleUploadDocument}
                    onToggleFlag={handleToggleFlag}
                    onDelete={handleDeleteDocument}
                    uploading={uploading}
                    onSaveInvioOrdinario={handleSaveInvioOrdinario}
                    disabled={false}
                    showNonNecessario={false}
                  />

                  {/* Post Esecutivo slot 2 (opzionale) */}
                  <DocumentSection
                    tipo="post_esecutivo_2"
                    label="Post Esecutivo 2"
                    ordineId={selectedOrdine.id}
                    documento={getDocumento(selectedOrdine.documenti, "post_esecutivo_2")}
                    onUpload={handleUploadDocument}
                    onToggleFlag={handleToggleFlag}
                    onDelete={handleDeleteDocument}
                    uploading={uploading}
                    onSaveInvioOrdinario={handleSaveInvioOrdinario}
                    disabled={false}
                    showNonNecessario={false}
                  />

                  {/* Post Esecutivo slot 3 (opzionale) */}
                  <DocumentSection
                    tipo="post_esecutivo_3"
                    label="Post Esecutivo 3"
                    ordineId={selectedOrdine.id}
                    documento={getDocumento(selectedOrdine.documenti, "post_esecutivo_3")}
                    onUpload={handleUploadDocument}
                    onToggleFlag={handleToggleFlag}
                    onDelete={handleDeleteDocument}
                    uploading={uploading}
                    onSaveInvioOrdinario={handleSaveInvioOrdinario}
                    disabled={false}
                    showNonNecessario={false}
                  />

                  {/* Condizioni Contrattuali */}
                  <DocumentSection
                    tipo="condizioni_contrattuali"
                    label="Condizioni Contrattuali"
                    ordineId={selectedOrdine.id}
                    documento={getDocumento(selectedOrdine.documenti, "condizioni_contrattuali")}
                    onUpload={handleUploadDocument}
                    onToggleFlag={handleToggleFlag}
                    onDelete={handleDeleteDocument}
                    uploading={uploading}
                    onSaveInvioOrdinario={handleSaveInvioOrdinario}
                    disabled={docusignFlags.condizioni_contrattuali || false}
                    showNonNecessario={true}
                    nonNecessario={documentiNonNecessari.condizioni_contrattuali || false}
                    onToggleNonNecessario={handleNonNecessarioToggle}
                    footerSlot={renderDocumentiStandardSection("condizioni_contrattuali")}
                  />

                  {/* Norme Uni */}
                  <DocumentSection
                    tipo="norme_uni"
                    label="Norme Uni"
                    ordineId={selectedOrdine.id}
                    documento={getDocumento(selectedOrdine.documenti, "norme_uni")}
                    onUpload={handleUploadDocument}
                    onToggleFlag={handleToggleFlag}
                    onDelete={handleDeleteDocument}
                    uploading={uploading}
                    onSaveInvioOrdinario={handleSaveInvioOrdinario}
                    disabled={docusignFlags.norme_uni || false}
                    showNonNecessario={true}
                    nonNecessario={documentiNonNecessari.norme_uni || false}
                    onToggleNonNecessario={handleNonNecessarioToggle}
                    footerSlot={renderDocumentiStandardSection("norme_uni")}
                  />

                  {/* Documenti Standard generici (senza sezione specifica) */}
                  {documentiStandard.some(d => (d.sezione || "generale") === "generale") && (
                    <div className="pt-4 mt-4 border-t-2 border-slate-200">
                      {renderDocumentiStandardSection("generale")}
                    </div>
                  )}

                  {/* Richiedi Documenti Personali */}
                  <div className="pt-4 mt-4 border-t-2 border-slate-200" data-testid="richiedi-doc-personali-section">
                    <div className="flex items-center gap-2 mb-3">
                      <Mail className="w-4 h-4 text-purple-600" />
                      <h4 className="font-semibold text-slate-800">Richiedi Documenti Personali</h4>
                    </div>
                    <p className="text-xs text-slate-600 mb-3">
                      Seleziona i documenti da richiedere al cliente. Riceverà una mail col link al Portale per caricarli.
                    </p>
                    <div className="space-y-2 mb-3 bg-purple-50 border border-purple-200 rounded-lg p-3">
                      <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-purple-600"
                          checked={reqDocPersonali.codice_fiscale}
                          onChange={(e) => setReqDocPersonali(p => ({ ...p, codice_fiscale: e.target.checked }))}
                          data-testid="req-doc-cf"
                        />
                        <span className="font-medium text-slate-800">Codice Fiscale</span>
                        <span className="text-xs text-slate-500">(Fronte e Retro)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                        <input
                          type="checkbox"
                          className="w-4 h-4 accent-purple-600"
                          checked={reqDocPersonali.documento_identita}
                          onChange={(e) => setReqDocPersonali(p => ({ ...p, documento_identita: e.target.checked }))}
                          data-testid="req-doc-id"
                        />
                        <span className="font-medium text-slate-800">Documento di Identità</span>
                        <span className="text-xs text-slate-500">(Fronte e Retro)</span>
                      </label>
                    </div>
                    {selectedOrdine?.documenti_personali_richiesti?.requested_at && (
                      <p className="text-xs text-emerald-700 mb-2 flex items-center gap-1">
                        <CheckCircle className="w-3 h-3" />
                        Ultima richiesta inviata il {new Date(selectedOrdine.documenti_personali_richiesti.requested_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    )}

                    {/* Flag "Segna come ricevuto" per ogni tipo richiesto */}
                    {selectedOrdine?.documenti_personali_richiesti?.requested_at && (
                      (selectedOrdine.documenti_personali_richiesti.codice_fiscale ||
                        selectedOrdine.documenti_personali_richiesti.documento_identita) && (
                        <div className="mb-3 bg-emerald-50 border border-emerald-200 rounded-lg p-3 space-y-2">
                          <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wide">
                            Segna come Ricevuto Manualmente
                          </p>
                          <p className="text-xs text-emerald-800">
                            Spunta se hai già ricevuto il documento a mano / via mail: lo rimuove dai promemoria e dalla sezione "Documenti Personali Mancanti".
                          </p>
                          {selectedOrdine.documenti_personali_richiesti.codice_fiscale && (
                            <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-emerald-600"
                                checked={!!selectedOrdine.documenti_personali_richiesti.codice_fiscale_ricevuto}
                                onChange={(e) => handleMarkDocPersonaleRicevuto("codice_fiscale", e.target.checked)}
                                data-testid="mark-cf-ricevuto"
                              />
                              <span className="font-medium text-slate-800">Codice Fiscale ricevuto</span>
                              {selectedOrdine.documenti_personali_richiesti.codice_fiscale_ricevuto_at && (
                                <span className="text-xs text-slate-500 italic">
                                  {new Date(selectedOrdine.documenti_personali_richiesti.codice_fiscale_ricevuto_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </label>
                          )}
                          {selectedOrdine.documenti_personali_richiesti.documento_identita && (
                            <label className="flex items-center gap-2 cursor-pointer text-sm select-none">
                              <input
                                type="checkbox"
                                className="w-4 h-4 accent-emerald-600"
                                checked={!!selectedOrdine.documenti_personali_richiesti.documento_identita_ricevuto}
                                onChange={(e) => handleMarkDocPersonaleRicevuto("documento_identita", e.target.checked)}
                                data-testid="mark-docid-ricevuto"
                              />
                              <span className="font-medium text-slate-800">Documento di Identità ricevuto</span>
                              {selectedOrdine.documenti_personali_richiesti.documento_identita_ricevuto_at && (
                                <span className="text-xs text-slate-500 italic">
                                  {new Date(selectedOrdine.documenti_personali_richiesti.documento_identita_ricevuto_at).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </span>
                              )}
                            </label>
                          )}
                        </div>
                      )
                    )}

                    <Button
                      onClick={handleInviaRichiestaDocPersonali}
                      disabled={invioReqDocPersonali || (!reqDocPersonali.codice_fiscale && !reqDocPersonali.documento_identita)}
                      className="bg-purple-600 hover:bg-purple-700 text-white"
                      data-testid="invia-richiesta-doc-personali-btn"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {invioReqDocPersonali ? "Invio…" : "Invia Richiesta"}
                    </Button>

                    {/* Riepilogo documenti caricati dal cliente */}
                    {(selectedOrdine?.documenti_personali_caricati || []).length > 0 && (
                      <div className="mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <p className="text-sm font-semibold text-emerald-800 mb-2 flex items-center gap-1">
                          <CheckCircle className="w-4 h-4" />
                          Documenti Caricati dal Cliente
                        </p>
                        <ul className="space-y-1 text-sm">
                          {(selectedOrdine.documenti_personali_caricati || []).map(doc => (
                            <li key={doc.slot} className="flex items-center justify-between gap-2">
                              <span className="text-slate-700 flex-1 truncate">
                                {doc.label} – <em className="text-slate-500">{doc.filename}</em>
                                {doc.data_caricamento && (
                                  <span className="block text-xs text-slate-500 not-italic">
                                    Caricato il {new Date(doc.data_caricamento).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                  </span>
                                )}
                              </span>
                              <a
                                href={`${API}/client/offer/${selectedOrdine.magic_link}/documenti-personali/${doc.slot}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`download-doc-personale-${doc.slot}`}
                              >
                                <Button size="sm" variant="outline" className="text-xs h-7">
                                  <Download className="w-3 h-3 mr-1" />
                                  Scarica
                                </Button>
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Documenti Extra (Certificazioni, Garanzia) */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-emerald-700">🏆 Documenti Extra (Certificazioni, Garanzia)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-xs text-slate-600">
                    Carica certificazioni, garanzie o altri documenti che vuoi rendere disponibili al cliente nel Portale (tab &quot;Contratto e Fatture&quot;).
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                    <div className="sm:col-span-1">
                      <Label className="text-xs">Tipo</Label>
                      <select
                        value={docExtraTipo}
                        onChange={(e) => setDocExtraTipo(e.target.value)}
                        className="w-full h-9 px-2 border border-slate-300 rounded-md text-sm"
                        data-testid="doc-extra-tipo"
                      >
                        <option value="certificazione">Certificazione</option>
                        <option value="garanzia">Garanzia</option>
                        <option value="altro">Altro</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <Label className="text-xs">Etichetta / descrizione (opzionale)</Label>
                      <Input
                        value={docExtraLabel}
                        onChange={(e) => setDocExtraLabel(e.target.value)}
                        placeholder="Es. Certificazione UNI EN 14351-1"
                        data-testid="doc-extra-label"
                      />
                    </div>
                  </div>
                  <FileDropZone
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,image/*"
                    dropLabel="Rilascia il documento"
                    onFile={(f) => setDocExtraFile(f)}
                    className="block"
                  >
                    <input
                      type="file"
                      onChange={(e) => setDocExtraFile(e.target.files?.[0] || null)}
                      className="text-sm w-full"
                      data-testid="doc-extra-file"
                    />
                  </FileDropZone>
                  <Button
                    size="sm"
                    onClick={handleUploadDocExtra}
                    disabled={!docExtraFile || docExtraUploading}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="upload-doc-extra-btn"
                  >
                    <Upload className="w-4 h-4 mr-1" />
                    {docExtraUploading ? "Caricamento…" : "Carica per il Cliente"}
                  </Button>

                  {(selectedOrdine.documenti_extra || []).length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-200">
                      <p className="text-sm font-semibold text-slate-700 mb-2">Documenti caricati:</p>
                      <ul className="space-y-1">
                        {(selectedOrdine.documenti_extra || []).map(d => (
                          <li key={d.id} className="flex items-center justify-between gap-2 p-2 bg-emerald-50 border border-emerald-200 rounded">
                            <div className="flex items-center gap-2 min-w-0">
                              <FileText className="w-4 h-4 text-emerald-600 shrink-0" />
                              <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase shrink-0">{d.tipo}</span>
                              <span className="text-sm truncate">{d.label}</span>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDeleteDocExtra(d.id)}
                              className="text-red-600 h-7 text-xs"
                              data-testid={`delete-doc-extra-${d.id}`}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Invio per Firma Elettronica */}<Card>
                <CardHeader>
                  <CardTitle className="text-lg">Invio per Firma Elettronica</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <p className="text-sm text-slate-600 mb-4">
                    Attiva i flag per inviare i documenti tramite piattaforma di firma elettronica (Docusign/Namirial)
                  </p>
                  
                  {/* Condizioni Contrattuali */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <label htmlFor="docusign-condizioni" className="font-medium text-slate-900 cursor-pointer">
                        Si Invia Docusign per Condizioni Contrattuali
                      </label>
                      <input
                        id="docusign-condizioni"
                        type="checkbox"
                        checked={docusignFlags.condizioni_contrattuali || false}
                        onChange={(e) => handleDocusignToggle("condizioni_contrattuali", e.target.checked)}
                        disabled={sendingDocusign}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                    </div>
                    
                    {docusignFlags.condizioni_contrattuali && (
                      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t">
                        <div>
                          <label className="text-sm text-slate-600 block mb-1">Data Invio</label>
                          <input
                            type="date"
                            value={docusignData.condizioni_contrattuali?.data_invio ? new Date(docusignData.condizioni_contrattuali.data_invio).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleDocusignDataChange("condizioni_contrattuali", "data_invio", e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={docusignData.condizioni_contrattuali?.ricevuto || false}
                              onChange={(e) => handleDocusignDataChange("condizioni_contrattuali", "ricevuto", e.target.checked)}
                              className="w-4 h-4 text-green-600 rounded mr-2"
                            />
                            <span className="text-sm font-medium text-slate-900">Documenti Ricevuti</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Norme Uni */}
                  <div className="border rounded-lg p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <label htmlFor="docusign-norme" className="font-medium text-slate-900 cursor-pointer">
                        Si Invia Docusign per Norme Uni
                      </label>
                      <input
                        id="docusign-norme"
                        type="checkbox"
                        checked={docusignFlags.norme_uni || false}
                        onChange={(e) => handleDocusignToggle("norme_uni", e.target.checked)}
                        disabled={sendingDocusign}
                        className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500 disabled:opacity-50"
                      />
                    </div>
                    
                    {docusignFlags.norme_uni && (
                      <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t">
                        <div>
                          <label className="text-sm text-slate-600 block mb-1">Data Invio</label>
                          <input
                            type="date"
                            value={docusignData.norme_uni?.data_invio ? new Date(docusignData.norme_uni.data_invio).toISOString().split('T')[0] : ''}
                            onChange={(e) => handleDocusignDataChange("norme_uni", "data_invio", e.target.value)}
                            className="w-full px-3 py-2 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <label className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={docusignData.norme_uni?.ricevuto || false}
                              onChange={(e) => handleDocusignDataChange("norme_uni", "ricevuto", e.target.checked)}
                              className="w-4 h-4 text-green-600 rounded mr-2"
                            />
                            <span className="text-sm font-medium text-slate-900">Documenti Ricevuti</span>
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Fatture e Collaudo */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    Fatture e Collaudo
                  </CardTitle>
                  <p className="text-sm text-slate-500">
                    Carica fatture e documenti di collaudo. Il cliente riceverà una notifica email.
                  </p>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Sezione Fatture */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-5 h-5 text-blue-600" />
                        <h4 className="font-semibold text-slate-800">Fatture</h4>
                      </div>
                      <FileDropZone
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={uploading}
                        onFile={(f) => handleUploadFatturaCollaudo(selectedOrdine.id, f, "fattura")}
                        dropLabel="Rilascia qui la Fattura"
                        className="inline-block"
                      >
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleUploadFatturaCollaudo(selectedOrdine.id, e.target.files[0], "fattura")}
                            disabled={uploading}
                          />
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors">
                            <Upload className="w-4 h-4" />
                            Carica Fattura
                          </span>
                        </label>
                      </FileDropZone>
                    </div>
                    {(selectedOrdine.fatture || []).length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Nessuna fattura caricata</p>
                    ) : (
                      <div className="space-y-2">
                        {(selectedOrdine.fatture || []).map((doc, index) => (
                          <div key={doc.file_path || `fattura-${index}`} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-blue-600" />
                              <div>
                                <p className="text-sm font-medium text-slate-800">{doc.original_filename}</p>
                                <p className="text-xs text-slate-500">{new Date(doc.data_caricamento).toLocaleDateString('it-IT')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API}/files/${doc.file_path}?download=true`}
                                className="p-1.5 text-slate-500 hover:text-blue-600"
                                title="Scarica"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => handleDeleteFatturaCollaudo(selectedOrdine.id, "fattura", index)}
                                className="p-1.5 text-slate-500 hover:text-red-600"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Sezione Collaudo */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-600" />
                        <h4 className="font-semibold text-slate-800">Collaudo</h4>
                      </div>
                      <FileDropZone
                        accept=".pdf,.jpg,.jpeg,.png"
                        disabled={uploading}
                        onFile={(f) => handleUploadFatturaCollaudo(selectedOrdine.id, f, "collaudo")}
                        dropLabel="Rilascia qui il Collaudo"
                        className="inline-block"
                      >
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleUploadFatturaCollaudo(selectedOrdine.id, e.target.files[0], "collaudo")}
                            disabled={uploading}
                          />
                          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-600 text-white text-sm rounded-lg hover:bg-emerald-700 transition-colors">
                            <Upload className="w-4 h-4" />
                            Carica Collaudo
                          </span>
                        </label>
                      </FileDropZone>
                    </div>
                    {(selectedOrdine.collaudo || []).length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-4">Nessun documento di collaudo caricato</p>
                    ) : (
                      <div className="space-y-2">
                        {(selectedOrdine.collaudo || []).map((doc, index) => (
                          <div key={doc.file_path || `collaudo-${index}`} className="flex items-center justify-between bg-slate-50 rounded-lg p-3">
                            <div className="flex items-center gap-3">
                              <FileText className="w-5 h-5 text-emerald-600" />
                              <div>
                                <p className="text-sm font-medium text-slate-800">{doc.original_filename}</p>
                                <p className="text-xs text-slate-500">{new Date(doc.data_caricamento).toLocaleDateString('it-IT')}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <a
                                href={`${API}/files/${doc.file_path}?download=true`}
                                className="p-1.5 text-slate-500 hover:text-emerald-600"
                                title="Scarica"
                              >
                                <Download className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => handleDeleteFatturaCollaudo(selectedOrdine.id, "collaudo", index)}
                                className="p-1.5 text-slate-500 hover:text-red-600"
                                title="Elimina"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Registro Email */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Registro Email Inviate</CardTitle>
                </CardHeader>
                <CardContent>
                  {loadingRegistro ? (
                    <div className="text-center py-4">
                      <div className="spinner inline-block" />
                      <p className="text-sm text-slate-500 mt-2">Caricamento registro...</p>
                    </div>
                  ) : registroEmail.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-4">
                      Nessuna email inviata per questo ordine
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {registroEmail.map((email, index) => {
                        const data = new Date(email.data);
                        const dataFormattata = data.toLocaleDateString('it-IT', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        });
                        const oraFormattata = data.toLocaleTimeString('it-IT', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        });

                        return (
                          <div key={email.data ? `${email.data}-${index}` : `email-${index}`} className="border rounded-lg p-4 bg-slate-50">
                            <div className="grid grid-cols-2 gap-3 text-sm mb-3">
                              <div>
                                <span className="text-slate-600 font-medium">Data:</span>
                                <p className="text-slate-900">{dataFormattata}</p>
                              </div>
                              <div>
                                <span className="text-slate-600 font-medium">Ora:</span>
                                <p className="text-slate-900">{oraFormattata}</p>
                              </div>
                              <div>
                                <span className="text-slate-600 font-medium">Mittente:</span>
                                <p className="text-slate-900 truncate">{email.mittente}</p>
                              </div>
                              <div>
                                <span className="text-slate-600 font-medium">Destinatario:</span>
                                <p className="text-slate-900 truncate">{email.destinatario}</p>
                              </div>
                            </div>
                            
                            <div className="mb-3">
                              <span className="text-slate-600 font-medium text-sm">Oggetto:</span>
                              <p className="text-slate-900 text-sm mt-1">{email.oggetto}</p>
                            </div>

                            {/* Allegato (Fattura PDF) */}
                            {email.allegato && email.allegato.download_url && (
                              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded flex items-center justify-between gap-2" data-testid={`registro-allegato-${index}`}>
                                <div className="flex items-center gap-2 min-w-0">
                                  <FileText className="w-4 h-4 text-blue-600 shrink-0" />
                                  <span className="text-sm text-slate-800 truncate">{email.allegato.filename}</span>
                                  {email.allegato.size_bytes && (
                                    <span className="text-xs text-slate-500 shrink-0">({(email.allegato.size_bytes/1024).toFixed(0)} KB)</span>
                                  )}
                                </div>
                                <a
                                  href={`${API}${email.allegato.download_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  data-testid={`registro-download-allegato-${index}`}
                                >
                                  <Button size="sm" variant="outline" className="text-xs h-7 border-blue-400 text-blue-700 hover:bg-blue-100">
                                    <Download className="w-3 h-3 mr-1" />
                                    Scarica Fattura
                                  </Button>
                                </a>
                              </div>
                            )}

                            <details className="group">
                              <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-2">
                                <span className="group-open:hidden">Mostra testo email</span>
                                <span className="hidden group-open:inline">Nascondi testo email</span>
                              </summary>
                              <div className="mt-3 bg-white rounded border overflow-hidden">
                                {email.body_html ? (
                                  <iframe
                                    title={`Email ${index}`}
                                    srcDoc={email.body_html}
                                    className="w-full h-[420px] bg-white border-0"
                                    sandbox=""
                                    data-testid={`registro-email-html-${index}`}
                                  />
                                ) : (
                                  <div className="p-3 text-sm whitespace-pre-wrap">
                                    {email.corpo}
                                  </div>
                                )}
                              </div>
                            </details>

                            <div className="mt-3 flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyRegistroEmailToClipboard(email)}
                                className="flex-1"
                                data-testid={`registro-copy-${index}`}
                              >
                                <Copy className="w-4 h-4 mr-2" />
                                Copia testo
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  const logId = email.id || String(index);
                                  const url = `${API}/ordini/${selectedOrdine.id}/registro-email/${logId}/pdf`;
                                  const link = document.createElement("a");
                                  link.href = url;
                                  document.body.appendChild(link);
                                  link.click();
                                  document.body.removeChild(link);
                                }}
                                className="flex-1"
                                data-testid={`registro-pdf-${index}`}
                              >
                                <Download className="w-4 h-4 mr-2" />
                                Scarica PDF
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Preview Email riutilizzabile (Richiesta Documenti / Contrattualistica / Documenti Integrativi) */}
      {emailPreview && (
        <EmailPreviewModal
          open={emailPreview.open}
          onClose={() => setEmailPreview(null)}
          title={emailPreview.title}
          endpoint={emailPreview.endpoint}
          basePayload={emailPreview.basePayload}
          successToast={emailPreview.successToast}
          onSent={emailPreview.onSent}
        />
      )}
    </PageLayout>
  );
}

// Componente per gestire una sezione documento
function DocumentSection({ tipo, label, ordineId, documento, onUpload, onToggleFlag, onDelete, uploading, disabled = false, showNonNecessario = false, nonNecessario = false, onToggleNonNecessario, onSaveInvioOrdinario, footerSlot = null }) {
  const [file, setFile] = useState(null);
  const [invioForm, setInvioForm] = useState({
    invio_ordinario_mail: documento?.invio_ordinario_mail || false,
    invio_ordinario_a_mano: documento?.invio_ordinario_a_mano || false,
    invio_ordinario_data: documento?.invio_ordinario_data || "",
    ricevuto_ordinario: documento?.ricevuto_ordinario || false,
    ricevuto_ordinario_data: documento?.ricevuto_ordinario_data || "",
    invio_ordinario_note: documento?.invio_ordinario_note || "",
  });
  const [savingInvio, setSavingInvio] = useState(false);

  useEffect(() => {
    setInvioForm({
      invio_ordinario_mail: documento?.invio_ordinario_mail || false,
      invio_ordinario_a_mano: documento?.invio_ordinario_a_mano || false,
      invio_ordinario_data: documento?.invio_ordinario_data || "",
      ricevuto_ordinario: documento?.ricevuto_ordinario || false,
      ricevuto_ordinario_data: documento?.ricevuto_ordinario_data || "",
      invio_ordinario_note: documento?.invio_ordinario_note || "",
    });
  }, [documento?.invio_ordinario_mail, documento?.invio_ordinario_a_mano, documento?.invio_ordinario_data, documento?.ricevuto_ordinario, documento?.ricevuto_ordinario_data, documento?.invio_ordinario_note]);

  const handleSaveInvio = async () => {
    if (!onSaveInvioOrdinario) return;
    setSavingInvio(true);
    try {
      await onSaveInvioOrdinario(tipo, invioForm);
    } finally {
      setSavingInvio(false);
    }
  };

  const handleFileChange = (e) => {
    if (disabled || nonNecessario) return;
    setFile(e.target.files[0]);
  };

  const handleUpload = () => {
    if (disabled || nonNecessario) return;
    onUpload(tipo, file, false); // Sempre false per default
    setFile(null);
  };

  // Se è marcato come non necessario, mostra uno stato speciale
  if (nonNecessario) {
    return (
      <div className="border rounded-lg p-4 space-y-3 bg-slate-50 border-slate-300">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold flex items-center gap-2 text-slate-500">
            {label}
            <span className="text-xs bg-slate-200 text-slate-600 px-2 py-1 rounded-full">
              Non Necessario
            </span>
          </h4>
          {showNonNecessario && (
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={nonNecessario}
                onChange={(e) => onToggleNonNecessario(tipo, e.target.checked)}
                className="w-4 h-4 text-slate-600 rounded mr-2"
              />
              <span className="text-xs text-slate-600">Non Necessario</span>
            </label>
          )}
        </div>
        <div className="text-center py-2 text-sm text-slate-500">
          <p>Questo documento è stato segnato come non necessario per questo ordine</p>
        </div>
      </div>
    );
  }

  const isAmbrato = tipo === "condizioni_contrattuali" || tipo === "norme_uni";
  const ambratoCls = isAmbrato ? "bg-amber-50 border-amber-300" : "";

  return (
    <div className={`border rounded-lg p-4 space-y-3 ${disabled ? 'bg-slate-100 opacity-60' : ambratoCls}`}>
      <div className="flex items-center justify-between">
        <h4 className="font-semibold flex items-center gap-2">
          {label}
          {disabled && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              📧 Inviato via Docusign
            </span>
          )}
        </h4>
        <div className="flex items-center gap-3">
          {showNonNecessario && !documento && (
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={nonNecessario}
                onChange={(e) => onToggleNonNecessario(tipo, e.target.checked)}
                className="w-4 h-4 text-slate-600 rounded mr-2"
              />
              <span className="text-xs text-slate-600">Non Necessario</span>
            </label>
          )}
          {/* Flag "Ricevuto Firmato" sempre visibile per consentire gestione manuale,
              anche quando il documento non è ancora caricato (Docusign, carta, ecc.) */}
          <button
            onClick={() => onToggleFlag(tipo, documento?.ricevuto_firmato || false)}
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              documento?.ricevuto_firmato
                ? "bg-green-100 text-green-700"
                : "bg-orange-100 text-orange-700"
            }`}
            data-testid={`toggle-flag-${tipo}`}
            title="Clicca per cambiare lo stato manualmente"
          >
            {documento?.ricevuto_firmato ? "✓ Ricevuto Firmato" : "⏳ In Attesa"}
          </button>
        </div>
      </div>

      {disabled ? (
        <div className="text-center py-4 text-sm text-slate-600">
          <p className="font-medium">Documento gestito tramite firma elettronica</p>
          <p className="text-xs mt-1">Il caricamento è disabilitato. Usa il badge sopra per segnarlo come ricevuto manualmente.</p>
        </div>
      ) : documento ? (
        <div className="space-y-2">
          <div className="flex items-center justify-between bg-slate-50 p-3 rounded">
            <div className="flex items-center gap-2 min-w-0">
              <FileText className="w-4 h-4 text-slate-600 shrink-0" />
              <div className="min-w-0">
                <span className="text-sm block truncate">{documento.original_filename || documento.file_path}</span>
                {documento.data_caricamento && (
                  <span className="text-xs text-slate-500">
                    Caricato il {new Date(documento.data_caricamento).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <a
                href={`${API}/ordini/${ordineId}/documento/${tipo}/download`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="sm"
                  variant="outline"
                  type="button"
                >
                  <Download className="w-4 h-4 mr-1" />
                  Scarica
                </Button>
              </a>
              <Button
                size="sm"
                variant="destructive"
                onClick={() => onDelete(tipo)}
              >
                Elimina
              </Button>
            </div>
          </div>
          {onSaveInvioOrdinario && (
            <InvioOrdinarioPanel
              invioForm={invioForm}
              setInvioForm={setInvioForm}
              onSave={handleSaveInvio}
              saving={savingInvio}
              tipo={tipo}
            />
          )}
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <Label>Carica documento</Label>
            <FileDropZone
              accept=".pdf,.doc,.docx"
              disabled={uploading}
              dropLabel="Rilascia qui il documento"
              onFile={(f) => setFile(f)}
              className="mt-1 block"
            >
              <label
                htmlFor={`file-${tipo}-${ordineId}`}
                className={`flex items-center justify-center gap-2 w-full px-3 py-2 border-2 border-dashed rounded-md text-sm cursor-pointer transition-colors ${
                  uploading
                    ? "border-slate-200 bg-slate-50 text-slate-400 cursor-not-allowed"
                    : "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 hover:border-blue-400"
                }`}
                data-testid={`file-label-${tipo}`}
              >
                <Upload className="w-4 h-4" />
                {file ? file.name : "Clicca o trascina qui il file"}
              </label>
              <input
                id={`file-${tipo}-${ordineId}`}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                disabled={uploading}
                className="hidden"
                data-testid={`file-input-${tipo}`}
              />
            </FileDropZone>
          </div>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full"
            data-testid={`upload-btn-${tipo}`}
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? "Caricamento..." : "Carica e Invia al Cliente"}
          </Button>
        </div>
      )}
      {!disabled && onSaveInvioOrdinario && !documento && (
        <InvioOrdinarioPanel
          invioForm={invioForm}
          setInvioForm={setInvioForm}
          onSave={handleSaveInvio}
          saving={savingInvio}
          tipo={tipo}
        />
      )}
      {footerSlot}
    </div>
  );
}

function InvioOrdinarioPanel({ invioForm, setInvioForm, onSave, saving, tipo }) {
  return (
    <div className="mt-3 border border-slate-200 rounded-lg p-3 bg-amber-50/40" data-testid={`invio-ordinario-${tipo}`}>
      <div className="flex items-center justify-between mb-2">
        <h5 className="text-sm font-semibold text-slate-800 flex items-center gap-1">
          📮 Invio Ordinario <span className="text-xs font-normal text-slate-500">(fuori CRM)</span>
        </h5>
        <Button
          size="sm"
          variant="outline"
          onClick={onSave}
          disabled={saving}
          className="bg-white text-xs h-7"
          data-testid={`save-invio-ordinario-${tipo}`}
        >
          {saving ? "Salvo..." : "Salva"}
        </Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={invioForm.invio_ordinario_mail}
                onChange={(e) => setInvioForm({ ...invioForm, invio_ordinario_mail: e.target.checked })}
                className="w-3.5 h-3.5"
                data-testid={`invio-mail-${tipo}`}
              />
              <span>Per Mail</span>
            </label>
            <label className="flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={invioForm.invio_ordinario_a_mano}
                onChange={(e) => setInvioForm({ ...invioForm, invio_ordinario_a_mano: e.target.checked })}
                className="w-3.5 h-3.5"
                data-testid={`invio-a-mano-${tipo}`}
              />
              <span>A Mano</span>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Data invio:</span>
            <input
              type="date"
              value={invioForm.invio_ordinario_data || ""}
              onChange={(e) => setInvioForm({ ...invioForm, invio_ordinario_data: e.target.value })}
              className="border border-slate-300 rounded px-1.5 py-0.5 text-xs"
              data-testid={`invio-data-${tipo}`}
            />
          </div>
        </div>
        <div className="space-y-1">
          <label className="flex items-center gap-1 cursor-pointer">
            <input
              type="checkbox"
              checked={invioForm.ricevuto_ordinario}
              onChange={(e) => setInvioForm({ ...invioForm, ricevuto_ordinario: e.target.checked })}
              className="w-3.5 h-3.5"
              data-testid={`ricevuto-ordinario-${tipo}`}
            />
            <span className="font-medium">Ricevuto Firmato</span>
          </label>
          <div className="flex items-center gap-2">
            <span className="text-slate-600">Data ricezione:</span>
            <input
              type="date"
              value={invioForm.ricevuto_ordinario_data || ""}
              onChange={(e) => setInvioForm({ ...invioForm, ricevuto_ordinario_data: e.target.value })}
              className="border border-slate-300 rounded px-1.5 py-0.5 text-xs"
              data-testid={`ricevuto-data-${tipo}`}
            />
          </div>
        </div>
      </div>
      {invioForm.invio_ordinario_data && !invioForm.ricevuto_ordinario && (
        <p className="text-[11px] text-amber-800 mt-2 italic">
          ⏰ Se non riceviamo il firmato entro 5 giorni dalla data invio, sarà inviato un sollecito automatico al cliente con te in copia.
        </p>
      )}
      <div className="mt-2">
        <label className="text-xs font-medium text-slate-700 flex items-center gap-1 mb-1">
          📝 Cosa hai Mandato <span className="text-slate-500 font-normal">(riassunto contenuto)</span>
        </label>
        <textarea
          value={invioForm.invio_ordinario_note || ""}
          onChange={(e) => setInvioForm({ ...invioForm, invio_ordinario_note: e.target.value })}
          placeholder="Es. Post esecutivo firmato + condizioni contrattuali, pagina 3 e 5 modificate a mano..."
          rows={2}
          className="w-full border border-slate-300 rounded px-2 py-1 text-xs bg-white resize-y focus:outline-none focus:ring-2 focus:ring-amber-300"
          data-testid={`invio-note-${tipo}`}
        />
      </div>
    </div>
  );
}

function DocumentiPersonaliMancanti() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [busy, setBusy] = useState(null);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ordini/documenti-personali-mancanti`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const markRicevuto = async (ordineId, tipo) => {
    setBusy(`${ordineId}-${tipo}`);
    try {
      await axios.put(
        `${API}/ordini/${ordineId}/documenti-personali/mark-ricevuto`,
        { tipo, ricevuto: true },
        { withCredentials: true }
      );
      toast.success("Documento segnato come ricevuto");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(null);
    }
  };

  const fmtIt = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "-";

  return (
    <Card className="mb-6 border-purple-300" data-testid="personali-mancanti-card">
      <CardHeader
        className="cursor-pointer hover:bg-purple-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-purple-600" />
            <span>Documenti Personali Mancanti</span>
            {!loading && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-purple-500 text-white text-xs font-bold">
                {items.length}
              </span>
            )}
          </div>
          <span className="text-xs font-normal text-slate-500">{expanded ? "▲ Chiudi" : "▼ Mostra elenco"}</span>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent>
          {items.length > 0 && (
            <div className="mb-3 flex items-center gap-2 bg-slate-50 rounded px-2 py-1 border border-slate-200">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtra per cognome, nome o email..."
                className="flex-1 bg-transparent outline-none text-sm py-1"
                data-testid="personali-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="bg-transparent text-xs text-slate-500 hover:text-slate-700"
                  data-testid="personali-search-clear"
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {loading ? (
            <p className="text-sm italic text-slate-500">Caricamento...</p>
          ) : items.length === 0 ? (
            <p className="text-sm italic text-emerald-700">🎉 Tutti i clienti hanno inviato i documenti personali richiesti!</p>
          ) : (() => {
              const q = search.trim().toLowerCase();
              const filtered = q
                ? items.filter((it) =>
                    `${it.cognome} ${it.nome} ${it.email}`.toLowerCase().includes(q)
                  )
                : items;
              if (filtered.length === 0) {
                return <p className="text-sm italic text-slate-500">Nessun cliente corrisponde al filtro "{search}"</p>;
              }
              return (
            <div className="divide-y divide-slate-200">
              {filtered.map((it) => (
                <div key={it.ordine_id} className="py-3" data-testid={`personali-mancanti-${it.ordine_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {it.cognome} {it.nome}
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          {it.email || "-"} {it.telefono && `· ${it.telefono}`}
                        </span>
                      </p>
                      <p className="text-xs text-slate-600">
                        {it.mancanti_count} tipo{it.mancanti_count === 1 ? "" : "i"} mancante{it.mancanti_count === 1 ? "" : "i"}
                        {it.requested_at && (
                          <span className="ml-2 text-slate-500">
                            · Richiesta inviata: {fmtIt(it.requested_at)}
                          </span>
                        )}
                        {it.reminder_sent_at && (
                          <span className="ml-2 text-slate-500">
                            · Ultimo promemoria: {fmtIt(it.reminder_sent_at)}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-1 pl-2">
                    {it.mancanti.map((m) => (
                      <div key={m.tipo} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-purple-50 border border-purple-200 rounded px-2 py-1.5">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-slate-800">🪪 {m.label}</span>
                          <span className="ml-2 text-xs text-slate-500">
                            mancano: {m.slots_mancanti.join(" + ")}
                          </span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            data-state="colored"
                            onClick={() => markRicevuto(it.ordine_id, m.tipo)}
                            disabled={busy === `${it.ordine_id}-${m.tipo}`}
                            style={{ backgroundColor: "#059669", color: "#ffffff" }}
                            className="text-xs font-medium px-2 py-1 rounded"
                            data-testid={`segna-ricevuto-personale-${it.ordine_id}-${m.tipo}`}
                          >
                            ✓ Segna come ricevuto
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
          })()}
        </CardContent>
      )}
    </Card>
  );
}

function DocumentiFirmatiDaCaricare() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [busyRow, setBusyRow] = useState(null);
  const [search, setSearch] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ordini/documenti-firmati-mancanti`, { withCredentials: true });
      setItems(res.data?.items || []);
    } catch (e) {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const segnaDoc = async (ordineId, tipo, metodo, disableReminder) => {
    setBusyRow(`${ordineId}-${tipo}`);
    try {
      await axios.post(
        `${API}/ordini/${ordineId}/documento/${encodeURIComponent(tipo)}/segna-manuale`,
        { metodo, disable_reminder: disableReminder },
        { withCredentials: true }
      );
      toast.success(metodo === "a_mano" ? "Segnato come consegnato a mano" : "Segnato come caricato");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusyRow(null);
    }
  };

  const toggleReminder = async (ordineId, nextDisabled) => {
    setBusyRow(`rem-${ordineId}`);
    try {
      await axios.patch(
        `${API}/ordini/${ordineId}/firmati-reminder`,
        { disabled: nextDisabled },
        { withCredentials: true }
      );
      toast.success(nextDisabled ? "Promemoria disattivato" : "Promemoria riattivato");
      fetchData();
    } catch (e) {
      toast.error("Errore aggiornamento promemoria");
    } finally {
      setBusyRow(null);
    }
  };

  const sendReminderNow = async (ordineId) => {
    setBusyRow(`now-${ordineId}`);
    try {
      const res = await axios.post(
        `${API}/ordini/${ordineId}/firmati-sollecito-ora`,
        {},
        { withCredentials: true }
      );
      toast.success(`Sollecito inviato a ${res.data?.sent_to} ✉️`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio sollecito");
    } finally {
      setBusyRow(null);
    }
  };

  const fmtIt = (iso) => iso ? new Date(iso).toLocaleDateString("it-IT") : "-";

  return (
    <Card className="mb-6 border-amber-300" data-testid="firmati-mancanti-card">
      <CardHeader
        className="cursor-pointer hover:bg-amber-50/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <CardTitle className="flex items-center justify-between text-base">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <span>Situazione Solleciti da Fare</span>
            {!loading && (
              <span className="inline-flex items-center justify-center min-w-[1.5rem] h-6 px-2 rounded-full bg-amber-500 text-white text-xs font-bold">
                {items.length}
              </span>
            )}
          </div>
          <span className="text-xs font-normal text-slate-500">{expanded ? "▲ Chiudi" : "▼ Mostra elenco"}</span>
        </CardTitle>
      </CardHeader>
      {expanded && (
        <CardContent>
          {/* Search box */}
          {items.length > 0 && (
            <div className="mb-3 flex items-center gap-2 bg-slate-50 rounded px-2 py-1 border border-slate-200">
              <Search className="w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filtra per cognome, nome o email..."
                className="flex-1 bg-transparent outline-none text-sm py-1"
                data-testid="firmati-search"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="bg-transparent text-xs text-slate-500 hover:text-slate-700"
                  data-testid="firmati-search-clear"
                >
                  ✕
                </button>
              )}
            </div>
          )}
          {loading ? (
            <p className="text-sm italic text-slate-500">Caricamento...</p>
          ) : items.length === 0 ? (
            <p className="text-sm italic text-emerald-700">🎉 Tutti i clienti hanno caricato i documenti firmati!</p>
          ) : (() => {
              const q = search.trim().toLowerCase();
              const filtered = q
                ? items.filter((it) =>
                    `${it.cognome} ${it.nome} ${it.email}`.toLowerCase().includes(q)
                  )
                : items;
              if (filtered.length === 0) {
                return <p className="text-sm italic text-slate-500">Nessun cliente corrisponde al filtro "{search}"</p>;
              }
              return (
            <div className="divide-y divide-slate-200">
              {filtered.map((it) => (
                <div key={it.ordine_id} className="py-3" data-testid={`firmati-mancanti-${it.ordine_id}`}>
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">
                        {it.cognome} {it.nome}
                        <span className="ml-2 text-xs font-normal text-slate-500">
                          {it.email || "-"} {it.telefono && `· ${it.telefono}`}
                        </span>
                        {it.in_attesa_dal && (() => {
                          const giorni = Math.max(0, Math.floor((Date.now() - new Date(it.in_attesa_dal)) / 86400000));
                          const cls = giorni > 14 ? "bg-red-100 text-red-700" : giorni > 7 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600";
                          return (
                            <span className={`ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`} data-testid={`firmati-giorni-attesa-${it.ordine_id}`}>
                              In attesa da {giorni} {giorni === 1 ? "giorno" : "giorni"}
                            </span>
                          );
                        })()}
                      </p>
                      <p className="text-xs text-slate-600">
                        {it.mancanti_count} su {it.totale_documenti} documenti mancanti
                        {it.firmati_reminder_sent_at && (
                          <span className="ml-2 text-slate-500">
                            · Ultimo promemoria: {fmtIt(it.firmati_reminder_sent_at)}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex gap-1 shrink-0 flex-wrap">
                      <button
                        type="button"
                        data-state="colored"
                        onClick={() => sendReminderNow(it.ordine_id)}
                        disabled={busyRow === `now-${it.ordine_id}` || it.firmati_reminder_disabled || !it.email}
                        style={{
                          backgroundColor: (it.firmati_reminder_disabled || !it.email) ? "#cbd5e1" : "#7c3aed",
                          color: "#ffffff",
                          cursor: (it.firmati_reminder_disabled || !it.email) ? "not-allowed" : "pointer",
                        }}
                        title={it.firmati_reminder_disabled ? "Promemoria disattivato" : (!it.email ? "Email cliente mancante" : "Invia subito l'email di sollecito")}
                        className="text-xs font-medium px-3 py-1.5 rounded"
                        data-testid={`send-now-${it.ordine_id}`}
                      >
                        {busyRow === `now-${it.ordine_id}` ? "⏳ Invio..." : "✉️ Manda sollecito ora"}
                      </button>
                      <button
                        type="button"
                        data-state="colored"
                        onClick={() => toggleReminder(it.ordine_id, !it.firmati_reminder_disabled)}
                        disabled={busyRow === `rem-${it.ordine_id}`}
                        style={{
                          backgroundColor: it.firmati_reminder_disabled ? "#16a34a" : "#dc2626",
                          color: "#ffffff",
                        }}
                        className="text-xs font-medium px-3 py-1.5 rounded"
                        data-testid={`toggle-reminder-${it.ordine_id}`}
                      >
                        {it.firmati_reminder_disabled ? "🔔 Riattiva Promemoria" : "🔕 Spegni Promemoria"}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-1 pl-2">
                    {it.mancanti.map((m) => (
                      <div key={m.tipo} className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
                        <div className="min-w-0">
                          <span className="text-sm font-medium text-slate-800">📄 {m.label}</span>
                          <span className="ml-2 text-xs text-slate-500">inviato {fmtIt(m.data_invio)}</span>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <button
                            type="button"
                            data-state="colored"
                            onClick={() => segnaDoc(it.ordine_id, m.tipo, "caricato", false)}
                            disabled={busyRow === `${it.ordine_id}-${m.tipo}`}
                            style={{ backgroundColor: "#2563eb", color: "#ffffff" }}
                            className="text-xs font-medium px-2 py-1 rounded"
                            data-testid={`segna-caricato-${it.ordine_id}-${m.tipo}`}
                          >
                            ✓ Caricato
                          </button>
                          <button
                            type="button"
                            data-state="colored"
                            onClick={() => segnaDoc(it.ordine_id, m.tipo, "a_mano", false)}
                            disabled={busyRow === `${it.ordine_id}-${m.tipo}`}
                            style={{ backgroundColor: "#059669", color: "#ffffff" }}
                            className="text-xs font-medium px-2 py-1 rounded"
                            data-testid={`segna-a-mano-${it.ordine_id}-${m.tipo}`}
                          >
                            ✋ A Mano
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          );
          })()}
        </CardContent>
      )}
    </Card>
  );
}
